import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import UserDocumentApi from '../../api/UserDocumentApi';
import { parseApiErrorResponse } from '../../utils/parseApiError';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Collapse,
  Divider,
  Stack,
  IconButton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tooltip,
  Link as MuiLink,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  Edit,
  Save,
  Cancel,
  Add,
  Delete,
  Link as LinkIcon,
  Info,
  ExpandMore,
} from '@mui/icons-material';
import './ManagerTicketView.scss';
import { ContextTicketDto, ticketStatusNames } from '../HomeView/HomeView';
import { UserDto } from '../../interfaces/UserInterfaces';
import ProsecutorSelect from '../../components/ProsecutorSelect/ProsecutorSelect';
import CourtSelect from '../../components/CourtSelect/CourtSelect';
import { FullProsecutorDto } from '../../api/ProsecutorApi';
import { FullCourtDto } from '../../api/CourtApi';
import CoverageApi, {
  CourtCoverageDto,
  CoveringAttorneyDto,
} from '../../api/CoverageApi';
import CourtEventApi, {
  APPEARANCE_LABELS,
  AppearanceRequirement,
  CourtEventDto,
  CreateCourtEventDto,
  UpdateCourtEventDto,
} from '../../api/CourtEventApi';
import {
  courtEventFormProblem as sharedCourtEventFormProblem,
  courtWallClockToMillis,
} from '../../utils/courtEventForm';
import CaseStatusTimeline from '../../components/CaseStatusTimeline/CaseStatusTimeline';
import CountyCasePanel, {
  AppealFeeState,
  LinkedCase,
} from '../../components/CountyCasePanel/CountyCasePanel';
import {
  JourneyFactKey,
  JourneyStamps,
} from '../../components/CaseStatusTimeline/caseJourney';
import { deriveStageIndex } from '../../utils/caseStage';
import { clientDisplayName } from '../../utils/clientName';
import CourtApi from '../../api/CourtApi';
import { formatDateOnly } from '../../utils/formatDateOnly';
import ManagerApi from '../../api/ManagerApi';
import ProsecutorApi from '../../api/ProsecutorApi';
import ReplyDraftApi from '../../api/ReplyDraftApi';
import ProductApi from '../../api/ProductApi';
import { ProductDto } from '../../interfaces/ProductInterfaces';
import { productMatchesTicketLicenseType } from '../../utils/productLicenseType';
import { OFFENSE_DESCRIPTION_OPTIONS } from '../../constants/offenseDescriptions';
import GenderPronounFields from '../../components/GenderPronounFields/GenderPronounFields';
import {
  formatGenderLabel,
  formatPronounSetLabel,
  PronounSet,
  TicketGender,
} from '../../utils/ticketGenderPronouns';
import ManagerTicketsList from '../../components/ManagerTicketsList/ManagerTicketsList';
import CaseDocumentsPanel from '../../components/CaseDocumentsPanel/CaseDocumentsPanel';
import CaseConsentChip from '../../components/CaseConsentChip/CaseConsentChip';
import AppealBondPanel from '../../components/AppealBondPanel/AppealBondPanel';
import AttorneyBondPanel from '../../components/AttorneyBondPanel/AttorneyBondPanel';
import BondRecordPanel from '../../components/BondRecordPanel/BondRecordPanel';
import FailureToAppearFeePanel from '../../components/FailureToAppearFeePanel/FailureToAppearFeePanel';
import ContinuanceMotionPanel from '../../components/ContinuanceMotionPanel/ContinuanceMotionPanel';
import RemoteAppearancePanel from '../../components/RemoteAppearancePanel/RemoteAppearancePanel';
import AbsenceMotionPanel from '../../components/AbsenceMotionPanel/AbsenceMotionPanel';
import WithdrawalMotionPanel from '../../components/WithdrawalMotionPanel/WithdrawalMotionPanel';
import SuretyWithdrawalPanel from '../../components/SuretyWithdrawalPanel/SuretyWithdrawalPanel';
import CaseConditionsPanel from '../../components/CaseConditionsPanel/CaseConditionsPanel';
import ClosingLetterPanel from '../../components/ClosingLetterPanel/ClosingLetterPanel';
import AlprReadinessBanner from '../../components/AlprReadinessBanner/AlprReadinessBanner';
import CaseDiscoveryPanel from '../../components/CaseDiscoveryPanel/CaseDiscoveryPanel';
import CaseDiscoveryRequestsPanel from '../../components/CaseDiscoveryRequestsPanel/CaseDiscoveryRequestsPanel';
import CaseCitationCard from '../../components/CaseCitationCard/CaseCitationCard';
import CaseShareLinkPanel from '../../components/CaseShareLinkPanel/CaseShareLinkPanel';
import CasePaymentsPanel from '../../components/CasePaymentsPanel/CasePaymentsPanel';
import CaseOfferPanel from '../../components/CaseOfferPanel/CaseOfferPanel';
import RejectOfferPanel from '../../components/RejectOfferPanel/RejectOfferPanel';
import CaseActivityPanel from '../../components/CaseActivityPanel/CaseActivityPanel';
import CaseReplyDraftsPanel from '../../components/CaseReplyDraftsPanel/CaseReplyDraftsPanel';
import SendUpdateDialog from '../../components/SendUpdateDialog/SendUpdateDialog';
import ApprovedContactsPanel from '../../components/ApprovedContactsPanel/ApprovedContactsPanel';
import {
  MATTER_TYPES,
  MATTER_TYPE_LABELS,
  NON_TRAFFIC_EXPLANATION,
  isTrafficMatter,
} from '../../constants/matterTypes';

// Enhanced interface to include ticket information
interface CourtEventWithTicket {
  CourtEvent_guid: string;
  CourtEvent_Start: number;
  CourtEvent_End: number;
  CourtEvent_Title: string;
  CourtEvent_Desc: string;
  CourtEvent_AppearanceRequirement?: AppearanceRequirement;
  CourtEvent_AppearanceSetBy?: string | null;
  CourtEvent_AppearanceSetAt?: string | null;
  CourtEvent_IsHearing?: boolean;
  Ticket_guid: string;
  ticketNumber: string;
  ticketGuid: string;
}

interface Ticket {
  Ticket_guid: string;
  Ticket_Number: string;
  Ticket_Address: string;
  Ticket_Citation: string;
  Ticket_Description: string | null;
  Ticket_OffenseDate: string | null;
  Ticket_LorTemplateOverride: string | null;
  Ticket_CauseNumber: string | null;
  Ticket_AgencyCaseNumber: string | null;
  Ticket_LicensePlate: string | null;
  Ticket_ArrestingAgency: string | null;
  Ticket_ArrestingAgencyAddress: string | null;
  Ticket_RespondByDate: string | null;
  Ticket_JuryTrialDate: string | null;
  // The discovery record (Lloyd, Aug 19). Edited on its own tab, not in the main edit form.
  Ticket_DiscoveryRequestedAt: string | null;
  Ticket_DiscoveryReceivedAt: string | null;
  Ticket_DiscoveryLocation: string | null;
  Ticket_DiscoveryNote: string | null;
  Ticket_City: string;
  Ticket_County: string;
  Ticket_LicenseType: string;
  Ticket_Zone: string;
  Ticket_Url: string;
  Ticket_Active: boolean;
  Ticket_CourtDate: string | null;
  Ticket_CreatedAt: string;
  User_id: number;
  currentStatus?: TicketStatus;
}

interface TicketStatus {
  TicketStatus_id: number;
  TicketStatus_DateTime: string;
  TicketStatus_Name: string;
  TicketStatus_Desc: string;
}

function getTicketApprovalBlockReason(
  ticketNumber?: string | null,
  matterType?: string | null,
): string | null {
  // A non-traffic matter has no citation and generates no LOR, so a ticket number is not something
  // it can have (Lloyd, Aug 6). The server stopped requiring one; without this the button would
  // still be blocked here and the case could not be approved from the UI at all.
  if (!isTrafficMatter(matterType)) {
    return null;
  }

  if (ticketNumber?.trim()) {
    return null;
  }

  return 'A ticket number must be entered before this ticket can be approved and the LOR generated.';
}


/**
 * The template codes are the single most misleading thing on this screen.
 *
 * "regular-over-25" is returned for BOTH a client over 25 years old AND a citation 25+ mph over the
 * limit, because both end up asking the court for a deferral. Maria Elena read it as the former and
 * re-made a correct letter by hand. Until the codes themselves are renamed, this says what the
 * letter actually is rather than what the code appears to say.
 */
/** The letters a manager can pick by hand, in the order they appear in the picker. */
const LOR_TEMPLATE_CHOICES = ['cdl', 'regular-over-25', 'regular-under-25'] as const;

function describeLorTemplate(templateType: string): string {
  switch (templateType) {
    case 'regular-under-25':
      return 'Standard letter — driving safety course available';
    case 'regular-over-25':
      return 'Deferral letter — driving safety course not available';
    case 'cdl':
      return 'CDL letter — not guilty, discovery and jury trial';
    default:
      return templateType;
  }
}


/**
 * The case tabs, in order, with the name each has in the page address (`?tab=documents`).
 * Order matters: TabPanel indexes below are positions in this list.
 */
const CASE_TABS = [
  { slug: 'history', label: 'Status History' },
  { slug: 'court-events', label: 'Court Events' },
  { slug: 'documents', label: 'Documents' },
  // Lloyd, 6 Oct 2026: "could you move all of the motions to its own tab?"
  { slug: 'motions', label: 'Motions' },
  { slug: 'discovery', label: 'Discovery' },
  { slug: 'payments', label: 'Payments' },
  { slug: 'offer', label: 'Offer' },
  // Notes, calls, emails and messages in one list (Lloyd, 6 Oct 2026). It replaced the separate
  // Emails, Messages and Notes tabs (Lloyd, 7 Oct: "Remove.").
  { slug: 'activity', label: 'Activity' },
  { slug: 'replies', label: 'Replies' },
] as const;

/** Addresses saved before Activity replaced these tabs still open on the same information. */
const RETIRED_TAB_SLUGS: Record<string, string> = {
  emails: 'activity',
  messages: 'activity',
  notes: 'activity',
};

/** A tab's position, by name, so the panels below cannot drift when a tab is added. */
function tabIndex(slug: (typeof CASE_TABS)[number]['slug']): number {
  return CASE_TABS.findIndex((tab) => tab.slug === slug);
}

/** This case's address on a given tab. A one-off court event highlight is dropped: it was for this visit. */
function caseTabHref(slug: string): string {
  const url = new URL(window.location.href);
  url.searchParams.delete('courtEvent');
  url.searchParams.set('tab', slug);
  return `${url.pathname}${url.search}`;
}

const ManagerTicketView: React.FC = () => {
  // const { ticketGuid } = useParams<{ ticketGuid: string }>();
  // Get the ticketGuid from the search params. courtEvent is optional: alert deep links
  // open the Court Events tab and mark that setting.
  const ticketSearch = new URLSearchParams(window.location.search);
  const ticketGuid = ticketSearch.get('Ticket_guid');
  const highlightedCourtEventGuid = ticketSearch.get('courtEvent');

  const navigate = useNavigate();

  const [ticket, setTicket] = useState<ContextTicketDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  /**
   * The chart facts that need offers, conditions and bonds.
   *
   * Empty until they load, and left empty if the request fails: an absent key reads as "not yet",
   * which draws the box hollow. A failed fetch therefore under-reports rather than claiming
   * something happened, which is the right way round for boxes staff read as a record.
   */
  const [journeyFacts, setJourneyFacts] = useState<Record<string, boolean>>({});
  // The dates behind the chart boxes staff stamp by hand, so the dialog can show and correct them.
  const [journeyStamps, setJourneyStamps] = useState<JourneyStamps>({});
  // The two cases of an appeal, each linking to the other (29 Sep 2026).
  const [countyCase, setCountyCase] = useState<LinkedCase | null>(null);
  const [appealedFrom, setAppealedFrom] = useState<LinkedCase | null>(null);
  const [appealFee, setAppealFee] = useState<AppealFeeState | null>(null);
  const [matterTypeSaving, setMatterTypeSaving] = useState(false);
  const [lorOverrideSaving, setLorOverrideSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // Non-blocking approval warnings from the server — chiefly "the court copy went to LOR@ because
  // this court's email is missing or a placeholder". Kept separate from successMessage so a warning
  // is not styled as an all-clear.
  const [approvalWarnings, setApprovalWarnings] = useState<string[]>([]);

  // Delete-case state (soft delete; Lloyd Jul 8)
  const [deleteCaseOpen, setDeleteCaseOpen] = useState(false);
  const [sendUpdateOpen, setSendUpdateOpen] = useState(false);
  const [deletingCase, setDeletingCase] = useState(false);

  // Soft-delete this case, then return to the previous screen (client profile / list).
  const handleDeleteCase = async () => {
    if (!ticketGuid) return;
    setDeletingCase(true);
    setError(null);
    try {
      const response = await fetch(
        `${process.env.API_HOST || ''}/api/v1/ticket/${ticketGuid}`,
        { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, credentials: 'include' },
      );
      if (!response.ok) {
        throw new Error(`Failed to delete case: ${response.status} ${response.statusText}`);
      }
      navigate(-1);
    } catch (err) {
      console.error('Error deleting case:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete case');
      setDeletingCase(false);
      setDeleteCaseOpen(false);
    }
  };

  // Form state for status update
  const [newStatus, setNewStatus] = useState('');
  const [statusDescription, setStatusDescription] = useState('');

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    Ticket_Number: '',
    Ticket_Citation: '',
    Ticket_Description: '',
    Ticket_OffenseDate: '',
    Ticket_CauseNumber: '',
    Ticket_AgencyCaseNumber: '',
    Ticket_LicensePlate: '',
    Ticket_ArrestingAgency: '',
    Ticket_ArrestingAgencyAddress: '',
    Ticket_RespondByDate: '',
    Ticket_JuryTrialDate: '',
    Ticket_Address: '',
    Ticket_City: '',
    Ticket_County: '',
    Ticket_LicenseType: '',
    Ticket_Zone: '',
    Ticket_CourtDate: '',
    Ticket_Representation: '',
    Ticket_Gender: '',
    Ticket_PronounSet: '',
    Ticket_HasTakenDscInPastYear: false,
  });
  /**
   * The client's date of birth, edited here as well as on the client record (Jett, 18 Sep 2026).
   *
   * Kept out of `editForm` on purpose: everything in there is spread straight into the case PATCH,
   * and this belongs to the client. It saves through the client route alongside the case, so one
   * Save button still does one save from a person's point of view.
   *
   * It is on the case screen because this is where its absence bites. Age picks the letter of
   * representation, so the letter cannot be generated without it, and the only box that could fix
   * that used to be on another screen.
   *
   * `clientDobAtLoad` is what makes the save safe. Without it, every case save re-asserted whatever
   * this screen last read, so editing only the city would silently overwrite a date somebody else
   * had corrected in the meantime — including clearing one, on a screen where the person had never
   * opened the date box.
   */
  const [clientDob, setClientDob] = useState('');
  const [clientDobAtLoad, setClientDobAtLoad] = useState('');
  const [selectedProsecutor, setSelectedProsecutor] =
    useState<FullProsecutorDto | null>(null);
  const [selectedCourt, setSelectedCourt] = useState<FullCourtDto | null>(null);
  // A tab named in the address opens on that tab, so a tab opened in a new browser tab lands where
  // it was clicked (Lloyd, 29 Sep 2026: "is there a way, when I click on one of the tabs, to have it
  // open in another tab?").
  const requestedSlug = ticketSearch.get('tab');
  const requestedTab = CASE_TABS.findIndex(
    (tab) => tab.slug === (RETIRED_TAB_SLUGS[requestedSlug ?? ''] ?? requestedSlug),
  );
  const [citationOpen, setCitationOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(
    requestedTab >= 0 ? requestedTab : highlightedCourtEventGuid ? 1 : 0,
  );
  /**
   * Whether the detail Lloyd asked to stop seeing is on screen (Aug 19).
   *
   * Collapsed by default and not remembered between cases: the point of the change is what the page
   * looks like the moment it opens. Entering edit mode expands it, because a field somebody cannot
   * see is a field they will report as missing.
   */
  const [showCaseData, setShowCaseData] = useState(false);
  const [products, setProducts] = useState<ProductDto[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Court Events state
  const [courtEvents, setCourtEvents] = useState<CourtEventDto[]>([]);
  const [courtEventsLoading, setCourtEventsLoading] = useState(false);
  const [coverageByEvent, setCoverageByEvent] = useState<Record<string, CourtCoverageDto>>({});
  const [coveringAttorneys, setCoveringAttorneys] = useState<CoveringAttorneyDto[]>([]);
  /** Distinguishes "nobody on file" from "we could not ask", which look identical as an empty list. */
  const [coverageLoadFailed, setCoverageLoadFailed] = useState(false);
  const [courtEventError, setCourtEventError] = useState<string | null>(null);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CourtEventDto | null>(null);

  /**
   * Does the court have a future setting for this case? (Lloyd, Aug 31 — Amadu Kamara.)
   *
   * Feeds the stage floor in deriveStageIndex. A court EVENT is used rather than
   * Ticket_CourtDate because only the event proves the court engaged — Ticket_CourtDate is
   * often just the appear-by date printed on the citation. See caseStage.ts.
   */
  const hasFutureCourtEvent = useMemo(() => {
    const now = Date.now();
    return courtEvents.some((event) => Number(event.CourtEvent_Start) > now);
  }, [courtEvents]);

  /**
   * Did the case's court date come from a court event, or only from the ticket?
   *
   * The chip said "Auto from court events" whenever the date was not set by hand, including on cases
   * with no court events at all, where the date is the appear-by date copied from the citation at
   * intake (Lloyd, 9 Oct 2026 — Beau Brown, 2026-09-0054). It now says so only when a court event
   * falls on that day.
   */
  const courtDateFromEvent = useMemo(() => {
    const courtDate = ticket?.ticket.Ticket_CourtDate;
    if (!courtDate) return false;
    const day = new Date(courtDate).toDateString();
    return courtEvents.some(
      (event) => new Date(Number(event.CourtEvent_Start)).toDateString() === day,
    );
  }, [courtEvents, ticket]);
  const [eventForm, setEventForm] = useState({
    CourtEvent_StartDate: '',
    CourtEvent_StartTime: '',
    CourtEvent_EndDate: '',
    CourtEvent_EndTime: '',
    CourtEvent_Title: '',
    CourtEvent_Desc: '',
  });
  /**
   * What an uploaded notice said about the client attending (meeting item 5, 30 Sep 2026). The
   * extraction read it all along and this screen dropped it, so staff ticked the box by hand after
   * saving. Null unless the notice said "must attend" in its own words; cleared whenever the dialog
   * opens, so one notice's answer never lands on the next date.
   */
  const [noticeAppearance, setNoticeAppearance] = useState<{
    requirement: AppearanceRequirement;
    quote: string | null;
  } | null>(null);

  // File upload state
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Court Info Modal state
  const [showCourtInfoModal, setShowCourtInfoModal] = useState(false);
  const [courtInfoLoading, setCourtInfoLoading] = useState(false);
  const [detailedCourtInfo, setDetailedCourtInfo] =
    useState<FullCourtDto | null>(null);
  const [detailedProsecutorInfo, setDetailedProsecutorInfo] =
    useState<FullProsecutorDto | null>(null);
  const [courtInfoError, setCourtInfoError] = useState<string | null>(null);

  // Function to check if the court event form is complete
  /**
   * The first thing wrong with the form, phrased for the person looking at it, or null when it is
   * good to save. Description is deliberately not required — a pre-trial notice often has nothing
   * to add beyond the date, time and title. Mirrors courtEventFormProblem in ManagerUserView.
   */
  // Shared with the client screen and the calendar, so the three cannot drift apart.
  const courtEventFormProblem = (): string | null =>
    sharedCourtEventFormProblem({
      startDate: eventForm.CourtEvent_StartDate,
      startTime: eventForm.CourtEvent_StartTime,
      endDate: eventForm.CourtEvent_EndDate,
      endTime: eventForm.CourtEvent_EndTime,
      title: eventForm.CourtEvent_Title,
    });

  const isCourtEventFormComplete = () => courtEventFormProblem() === null;

  // Function to check if end date/time is after start date/time
  const isEndTimeAfterStartTime = () => {
    if (
      !eventForm.CourtEvent_StartDate ||
      !eventForm.CourtEvent_StartTime ||
      !eventForm.CourtEvent_EndDate ||
      !eventForm.CourtEvent_EndTime
    ) {
      return true; // Allow if fields are empty (will be caught by other validation)
    }

    const startDateTime = new Date(
      `${eventForm.CourtEvent_StartDate}T${eventForm.CourtEvent_StartTime}`,
    );
    const endDateTime = new Date(
      `${eventForm.CourtEvent_EndDate}T${eventForm.CourtEvent_EndTime}`,
    );

    // End time must be strictly after start time (not equal)
    return endDateTime > startDateTime;
  };

  // Function to check if the entire form is valid
  const isCourtEventFormValid = () => {
    return isCourtEventFormComplete() && isEndTimeAfterStartTime();
  };

  const statusOptions = ticketStatusNames;

  // TabPanel component for tab content
  interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
  }

  function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
      </div>
    );
  }

  /** Switch tab and keep the address in step, so a reload or a copied link lands on the same tab. */
  const selectTab = (index: number) => {
    setActiveTab(index);
    const slug = CASE_TABS[index]?.slug;
    if (!slug) return;
    const url = new URL(window.location.href);
    url.searchParams.set('tab', slug);
    window.history.replaceState(window.history.state, '', url.toString());
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    selectTab(newValue);
    // Load court events when the events tab (index 1 for User Tickets tab) is selected
    // Note: Court Events are now in the User Tickets tab, so we load them when tab 0 is selected
    if (newValue === 0 && ticket) {
      loadCourtEvents();
    }
  };

  const loadCourtEvents = async () => {
    console.log('ticket');
    console.log(ticket);
    if (!ticket) return;

    setCourtEventsLoading(true);
    setCourtEventError(null);

    try {
      const events = await CourtEventApi.listCourtEvents({
        Ticket_guid: ticket.ticket.Ticket_guid,
      });
      setCourtEvents(events);
    } catch (error) {
      setCourtEventError(
        error instanceof Error ? error.message : 'Failed to load court events',
      );
    } finally {
      setCourtEventsLoading(false);
    }
  };

  /**
   * Who is covering the settings on this case, and who could.
   *
   * Loaded here rather than inside each court-event row: a case with a dozen settings would
   * otherwise make a dozen requests to draw one chip each. A failure is left silent because
   * coverage is an extra on this screen — the settings themselves still have to render.
   */
  const loadCoverage = async () => {
    if (!ticket) return;
    try {
      const [recorded, people] = await Promise.all([
        CoverageApi.listCoverage({ ticketGuid: ticket.ticket.Ticket_guid }),
        CoverageApi.listAttorneys(),
      ]);
      const byEvent: Record<string, CourtCoverageDto> = {};
      for (const row of recorded) {
        // Docket appearances carry no setting and belong on the coverage screen, not on a row here.
        if (row.courtEventGuid) byEvent[row.courtEventGuid] = row;
      }
      setCoverageByEvent(byEvent);
      setCoveringAttorneys(people);
      setCoverageLoadFailed(false);
    } catch {
      /*
       * Failed, not empty. Setting the list to `[]` made the control say "no covering attorneys on
       * file yet", which is a statement about the database that we have no basis for — the request
       * did not come back. The control renders nothing at all in this state.
       */
      setCoverageLoadFailed(true);
    }
  };

  const handleCreateEvent = () => {
    setEditingEvent(null);
    setEventForm({
      CourtEvent_StartDate: '',
      CourtEvent_StartTime: '',
      CourtEvent_EndDate: '',
      CourtEvent_EndTime: '',
      CourtEvent_Title: '',
      CourtEvent_Desc: '',
    });
    setNoticeAppearance(null);
    setSelectedFile(null);
    setUploadingFile(false);
    setShowEventDialog(true);
  };

  const handleFileUpload = async (file: File) => {
    if (!ticket?.user?.User_guid) return;

    setUploadingFile(true);
    setSelectedFile(file);

    try {
      const uploadResult = await UserDocumentApi.uploadDocument(
        ticket.user.User_guid,
        file,
        true, // Enable OCR (legacy fallback)
        ticket?.ticket?.Ticket_guid, // file the document under this case
        true, // run the vision/LLM calendar extraction (court-event flow)
      );

      // Prefer the vision/LLM calendar extraction (reads dates straight off the scan);
      // fall back to the legacy OCR date heuristics when it's unavailable.
      const detected = uploadResult.calendarExtraction?.events ?? [];
      if (detected.length > 0) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const chosen =
          detected.find((e) => new Date(`${e.date}T00:00:00`) >= today) ??
          detected[0];

        const updates: any = {
          CourtEvent_StartDate: chosen.date,
          CourtEvent_EndDate: chosen.date,
        };
        if (chosen.time) {
          updates.CourtEvent_StartTime = chosen.time;
          const [hours, minutes] = chosen.time.split(':').map(Number);
          const endHour = Math.min(hours + 2, 23);
          updates.CourtEvent_EndTime = `${endHour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        }
        if (chosen.title) updates.CourtEvent_Title = chosen.title;
        const desc =
          chosen.description || (chosen.court ? `Court: ${chosen.court}` : '');
        if (desc) updates.CourtEvent_Desc = desc;

        setEventForm((prev) => ({ ...prev, ...updates }));
        setNoticeAppearance(
          chosen.suggestedAppearance
            ? { requirement: chosen.suggestedAppearance, quote: chosen.appearanceQuote ?? null }
            : null,
        );

        if (uploadResult.calendarExtraction?.confidence === 'low') {
          setError(
            'AI confidence was low on this scan — please verify the date and time before saving.',
          );
        }
      } else if (uploadResult.ocr) {
        // Process OCR dates - find first future date
        const currentDate = new Date();
        const futureDate = uploadResult.ocr.dates
          .map((dateStr) => new Date(dateStr))
          .filter((date) => date > currentDate)
          .sort((a, b) => a.getTime() - b.getTime())[0];

        // Process OCR times - find earliest time between 9am-6pm
        const validTime = uploadResult.ocr.times
          .filter((timeStr) => {
            const [hours] = timeStr.split(':').map(Number);
            return hours >= 9 && hours <= 18; // 9am to 6pm
          })
          .sort()[0]; // Sort to get earliest time

        // Update form with OCR data
        const updates: any = {};

        if (futureDate) {
          const startDate = futureDate.toISOString().split('T')[0];
          updates.CourtEvent_StartDate = startDate;
          updates.CourtEvent_EndDate = startDate; // Same date
        }

        if (validTime) {
          updates.CourtEvent_StartTime = validTime;
          // Add 2 hours for end time
          const [hours, minutes] = validTime.split(':').map(Number);
          const endHour = Math.min(hours + 2, 23); // Don't go past 23:59
          updates.CourtEvent_EndTime = `${endHour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        }

        // Set title based on trial type
        if (uploadResult.ocr.trialType) {
          updates.CourtEvent_Title =
            uploadResult.ocr.trialType === 'pretrial'
              ? 'Pretrial Hearing'
              : 'Trial';
        }

        if (Object.keys(updates).length > 0) {
          setEventForm((prev) => ({ ...prev, ...updates }));
        }
      }
    } catch (error) {
      console.error('File upload failed:', error);
      // Don't show error to user, just log it
    } finally {
      setUploadingFile(false);
    }
  };

  // Handle drag-and-drop file upload from ManagerTicketsList
  const handleCreateEventWithFile = async (file: File, ticketGuid: string) => {
    try {
      // Verify this is the correct ticket
      if (ticket?.ticket?.Ticket_guid !== ticketGuid) {
        throw new Error('Ticket mismatch');
      }

      // Reset form and prepare for new event creation
      setEditingEvent(null);
      setEventForm({
        CourtEvent_Title: '',
        CourtEvent_StartDate: '',
        CourtEvent_StartTime: '',
        CourtEvent_EndDate: '',
        CourtEvent_EndTime: '',
        CourtEvent_Desc: '',
      });
      setNoticeAppearance(null);

      // Open the create event dialog first
      setShowEventDialog(true);

      // Process the file and populate form with OCR data (this will happen in the background)
      await handleFileUpload(file);
    } catch (error) {
      console.error('Error creating event with file:', error);
      // Still try to open the dialog for manual entry
      setShowEventDialog(true);
    }
  };

  const handleEditEvent = (event: CourtEventWithTicket) => {
    setEditingEvent(event);
    // An existing date answers appearance on its own control, not from a notice.
    setNoticeAppearance(null);
    // Convert epoch time back to date/time strings for form inputs
    const startDate = new Date(parseInt('' + event.CourtEvent_Start));
    const endDate = new Date(parseInt('' + event.CourtEvent_End));

    setEventForm({
      CourtEvent_StartDate: startDate.toISOString().split('T')[0], // YYYY-MM-DD
      CourtEvent_StartTime: startDate.toTimeString().split(' ')[0].slice(0, 5), // HH:MM
      CourtEvent_EndDate: endDate.toISOString().split('T')[0], // YYYY-MM-DD
      CourtEvent_EndTime: endDate.toTimeString().split(' ')[0].slice(0, 5), // HH:MM
      CourtEvent_Title: event.CourtEvent_Title,
      CourtEvent_Desc: event.CourtEvent_Desc,
    });
    setShowEventDialog(true);
  };

  const handleSaveEvent = async () => {
    if (!ticket) return;

    // Additional validation before submission
    const problem = courtEventFormProblem();
    if (problem) {
      setError(problem);
      return;
    }

    setActionLoading(true);
    try {
      /*
       * Court wall-clock, not the browser's zone. "9:00 AM" on a notice means nine in a Texas
       * courtroom, and `new Date("...T09:00")` read it as nine wherever the laptop was set.
       * Same defect the server fixed in utils/courtTimezone.ts.
       */
      const startEpoch = courtWallClockToMillis(
        eventForm.CourtEvent_StartDate,
        eventForm.CourtEvent_StartTime,
      );
      const endEpoch = courtWallClockToMillis(
        eventForm.CourtEvent_EndDate,
        eventForm.CourtEvent_EndTime,
      );

      if (startEpoch === null || endEpoch === null) {
        setError('Check the date and time.');
        setActionLoading(false);
        return;
      }

      // Double-check that end time is after start time
      if (endEpoch <= startEpoch) {
        setError('End date and time must be after start date and time.');
        setActionLoading(false);
        return;
      }

      if (editingEvent) {
        // Update existing event
        const updateData: UpdateCourtEventDto = {
          CourtEvent_guid: editingEvent.CourtEvent_guid,
          CourtEvent_Start: startEpoch,
          CourtEvent_End: endEpoch,
          CourtEvent_Title: eventForm.CourtEvent_Title,
          CourtEvent_Desc: eventForm.CourtEvent_Desc,
        };
        await CourtEventApi.updateCourtEvent(updateData);
        setSuccessMessage('Court event updated successfully');
      } else {
        // Create new event
        const createData: CreateCourtEventDto = {
          Ticket_guid: ticket.ticket.Ticket_guid,
          CourtEvent_Start: startEpoch,
          CourtEvent_End: endEpoch,
          CourtEvent_Title: eventForm.CourtEvent_Title,
          CourtEvent_Desc: eventForm.CourtEvent_Desc,
          ...(noticeAppearance
            ? { CourtEvent_AppearanceRequirement: noticeAppearance.requirement }
            : {}),
        };
        const created = await CourtEventApi.createCourtEvent(createData);
        // Say what reached the client rather than implying an email went.
        const notice = created.appearanceNotification;
        setSuccessMessage(
          notice?.sent
            ? 'Court event created, and the client was emailed that they must attend'
            : notice
              ? `Court event created. The client was not emailed: ${notice.skippedReason}`
              : 'Court event created successfully',
        );
      }

      setShowEventDialog(false);
      loadCourtEvents(); // Reload events
    } catch (error) {
      setError(
        error instanceof Error ? error.message : 'Failed to save court event',
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteEvent = async (event: CourtEventWithTicket) => {
    if (!confirm('Are you sure you want to delete this court event?')) return;

    setActionLoading(true);
    try {
      await CourtEventApi.deleteCourtEvent(event.CourtEvent_guid);
      setSuccessMessage('Court event deleted successfully');
      loadCourtEvents(); // Reload events
    } catch (error) {
      setError(
        error instanceof Error ? error.message : 'Failed to delete court event',
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenCourtInfo = async () => {
    setShowCourtInfoModal(true);
    setCourtInfoLoading(true);
    setCourtInfoError(null);
    setDetailedCourtInfo(null);
    setDetailedProsecutorInfo(null);

    try {
      // Fetch detailed court information if court exists
      if (ticket?.court?.Court_guid) {
        try {
          const courtResponse = await CourtApi.getCourtById(
            ticket.court.Court_guid,
          );
          setDetailedCourtInfo(courtResponse.court);
        } catch (error) {
          console.error('Failed to fetch court details:', error);
        }
      }

      // Fetch detailed prosecutor information if prosecutor exists
      if (ticket?.prosecutor?.Prosecutor_guid) {
        try {
          const prosecutorResponse = await ProsecutorApi.getProsecutorById(
            ticket.prosecutor.Prosecutor_guid,
          );
          setDetailedProsecutorInfo(prosecutorResponse.prosecutor);
        } catch (error) {
          console.error('Failed to fetch prosecutor details:', error);
        }
      }
    } catch (error) {
      setCourtInfoError(
        error instanceof Error
          ? error.message
          : 'Failed to load court information',
      );
    } finally {
      setCourtInfoLoading(false);
    }
  };

  const handleCloseCourtInfo = () => {
    setShowCourtInfoModal(false);
    setDetailedCourtInfo(null);
    setDetailedProsecutorInfo(null);
    setCourtInfoError(null);
  };

  const handleViewCourt = () => {
    if (detailedCourtInfo) {
      navigate(`/court?Court_guid=${detailedCourtInfo.Court_guid}`);
    }
  };

  const formatTime = (timeString: string) => {
    try {
      // Convert 24-hour format to 12-hour format
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  const formatEpochDate = (epochTime: number) => {
    try {
      return new Date(parseInt('' + epochTime)).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return 'Invalid date';
    }
  };

  const formatEpochTime = (epochTime: number) => {
    try {
      return new Date(parseInt('' + epochTime)).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return 'Invalid time';
    }
  };

  const loadJourney = useCallback(async (guid: string, isCancelled: () => boolean = () => false) => {
    try {
      const apiHost = process.env.API_HOST?.trim() || window.location.origin;
      const response = await fetch(
        `${apiHost}/api/v1/manager/ticket/${guid}/journey-facts`,
        { credentials: 'include' },
      );
      if (!response.ok) return;
      const body = await response.json();
      if (isCancelled()) return;
      setJourneyFacts(body?.facts ?? {});
      setJourneyStamps(body?.stamps ?? {});
      setCountyCase(body?.countyCase ?? null);
      setAppealedFrom(body?.appealedFrom ?? null);
      setAppealFee(body?.appealFee ?? null);
    } catch {
      // Leave the boxes hollow. See the note on journeyFacts.
    }
  }, []);

  useEffect(() => {
    const guid = ticket?.ticket?.Ticket_guid;
    if (!guid) return;
    let cancelled = false;
    // Following a link between the two cases of an appeal keeps this page mounted; without this the
    // previous case's link would show until the new one loads.
    setCountyCase(null);
    setAppealedFrom(null);
    setAppealFee(null);
    loadJourney(guid, () => cancelled);
    return () => {
      cancelled = true;
    };
  }, [ticket?.ticket?.Ticket_guid, loadJourney]);

  /** Stamp or clear one hand-recorded chart step, then re-read the chart so the box reflects it. */
  const saveJourneyStamp = async (key: JourneyFactKey, date: string | null) => {
    const guid = ticket?.ticket?.Ticket_guid;
    if (!guid) return;
    const apiHost = process.env.API_HOST?.trim() || window.location.origin;
    const response = await fetch(`${apiHost}/api/v1/manager/ticket/${guid}/journey-stamps`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ [key]: date }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Could not record this step.');
    }
    await loadJourney(guid);
  };

  useEffect(() => {
    if (ticketGuid) {
      fetchTicket();
    } else {
      setError('No ticket ID provided in URL');
      setLoading(false);
    }
  }, [ticketGuid]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoadingProducts(true);
      try {
        const result = await ProductApi.getProducts({ from: 0, to: 100 });
        setProducts(result.data);
      } catch (err) {
        console.error('Error fetching products:', err);
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchProducts();
  }, []);

  const activeLicenseType = isEditing
    ? editForm.Ticket_LicenseType
    : (ticket?.ticket.Ticket_LicenseType ?? '');

  const matchingProducts = useMemo(
    () =>
      products.filter((product) =>
        productMatchesTicketLicenseType(
          product.Product_LicenseType,
          activeLicenseType,
        ),
      ),
    [products, activeLicenseType],
  );

  useEffect(() => {
    if (!isEditing || !editForm.Ticket_Representation) return;

    const selectedProduct = products.find(
      (product) => product.Product_Name === editForm.Ticket_Representation,
    );
    if (
      selectedProduct &&
      !productMatchesTicketLicenseType(
        selectedProduct.Product_LicenseType,
        editForm.Ticket_LicenseType,
      )
    ) {
      setEditForm((prev) => ({ ...prev, Ticket_Representation: '' }));
    }
  }, [
    editForm.Ticket_LicenseType,
    editForm.Ticket_Representation,
    isEditing,
    products,
  ]);

  // Populate edit form when ticket data changes
  useEffect(() => {
    if (ticket) {
      setEditForm({
        Ticket_Number: ticket.ticket.Ticket_Number,
        Ticket_Citation: ticket.ticket.Ticket_Citation,
        Ticket_Description: ticket.ticket.Ticket_Description || '',
        Ticket_OffenseDate: ticket.ticket.Ticket_OffenseDate
          ? String(ticket.ticket.Ticket_OffenseDate).slice(0, 10)
          : '',
        Ticket_CauseNumber: ticket.ticket.Ticket_CauseNumber ?? '',
        Ticket_AgencyCaseNumber: ticket.ticket.Ticket_AgencyCaseNumber ?? '',
        Ticket_LicensePlate: ticket.ticket.Ticket_LicensePlate ?? '',
        Ticket_ArrestingAgency: ticket.ticket.Ticket_ArrestingAgency ?? '',
        Ticket_ArrestingAgencyAddress:
          ticket.ticket.Ticket_ArrestingAgencyAddress ?? '',
        Ticket_RespondByDate: ticket.ticket.Ticket_RespondByDate
          ? String(ticket.ticket.Ticket_RespondByDate).slice(0, 10)
          : '',
        Ticket_JuryTrialDate: ticket.ticket.Ticket_JuryTrialDate
          ? String(ticket.ticket.Ticket_JuryTrialDate).slice(0, 10)
          : '',
        Ticket_Address: ticket.ticket.Ticket_Address,
        Ticket_City: ticket.ticket.Ticket_City,
        Ticket_County: ticket.ticket.Ticket_County,
        Ticket_LicenseType: ticket.ticket.Ticket_LicenseType,
        Ticket_Zone: ticket.ticket.Ticket_Zone,
        Ticket_CourtDate: formatDate(ticket.ticket.Ticket_CourtDate),
        Ticket_Representation: ticket.ticket.Ticket_Representation,
        Ticket_Gender: ticket.ticket.Ticket_Gender ?? '',
        Ticket_PronounSet: ticket.ticket.Ticket_PronounSet ?? '',
        Ticket_HasTakenDscInPastYear:
          ticket.ticket.Ticket_HasTakenDscInPastYear === true,
      });
      const dobOnFile = (ticket.user?.User_DOB ?? '').slice(0, 10);
      setClientDob(dobOnFile);
      setClientDobAtLoad(dobOnFile);
      setSelectedProsecutor(ticket.prosecutor || null);
      // Set selectedCourt from the ticket's court data
      if (ticket.court) {
        setSelectedCourt({
          ...ticket.court,
          prosecutors: [], // Empty array since we don't have this data
        });
      } else {
        setSelectedCourt(null);
      }
      loadCourtEvents();
      loadCoverage();
    }
  }, [ticket]);

  const fetchTicket = async () => {
    try {
      setLoading(true);
      setError(null); // Clear previous errors

      const response = await fetch(
        (process.env.API_HOST || '') + `/api/v1/manager/ticket/${ticketGuid}`,
        {
          credentials: 'include',
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.log('Error response:', errorText);
        throw new Error(
          `Failed to fetch ticket: ${response.status} ${response.statusText}`,
        );
      }

      const data: ContextTicketDto = await response.json();
      setTicket(data);
    } catch (err) {
      console.error('Error in fetchTicket:', err);
      setError(err instanceof Error ? err.message : 'Failed to load ticket');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Switch a case between traffic and non-traffic (Lloyd, Aug 6).
   *
   * Reports what the change did rather than saving silently. Turning a case non-traffic stops the
   * letter of representation being generated at approval, and someone who was not told that would
   * reasonably wait for a letter that is never coming.
   */
  const handleMatterTypeChange = async (next: string) => {
    if (!ticket) return;
    setMatterTypeSaving(true);
    setError(null);
    try {
      const result = await ReplyDraftApi.setMatterType(
        ticket.ticket.Ticket_guid,
        next,
      );
      setTicket({
        ...ticket,
        ticket: { ...ticket.ticket, Ticket_MatterType: result.Ticket_MatterType },
      });
      setSuccessMessage(
        result.trafficAutomation
          ? 'Case set to traffic. The usual letter of representation and reminders apply.'
          : `Case set to non-traffic. ${result.note ?? NON_TRAFFIC_EXPLANATION}`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set the case type');
    } finally {
      setMatterTypeSaving(false);
    }
  };

  const handleApproveTicket = async () => {
    const approvalBlockReason = ticket
      ? getTicketApprovalBlockReason(
          ticket.ticket.Ticket_Number,
          ticket.ticket.Ticket_MatterType,
        )
      : null;

    if (approvalBlockReason) {
      setSuccessMessage(null);
      setError(approvalBlockReason);
      return;
    }

    try {
      setActionLoading(true);
      setError(null);
      setSuccessMessage(null);

      const response = await fetch(
        (process.env.API_HOST || '') +
          `/api/v1/manager/ticket/${ticketGuid}/approve`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response.ok) {
        let message = 'Failed to approve ticket';
        try {
          const errorData = await response.json();
          message = errorData.message || errorData.error || message;
        } catch {
          // Response body was not JSON
        }
        throw new Error(message);
      }

      const data = (await response.json()) as ContextTicketDto & {
        approved?: boolean;
        lorDelivery?: {
          mode: 'dev-review' | 'outlook-drafts';
          reviewFile?: { fileName: string; filePath: string };
          courtDeliveryMode?: 'draft' | 'send-to-court' | 'send-to-lor-internal';
          courtEmailStatus?: 'valid' | 'placeholder' | 'missing';
          courtDeliveredTo?: string | null;
        };
        warnings?: string[];
        deliverySummary?: string | null;
      };
      setTicket(data);
      setApprovalWarnings(data.warnings ?? []);
      if (data.lorDelivery?.mode === 'dev-review') {
        setSuccessMessage(
          `LOR PDF saved for dev review (ticket not approved): ${data.lorDelivery.reviewFile?.fileName ?? 'see backend/temp/testing_storage'}`,
        );
      } else {
        // Name where each copy actually went. The old message always claimed the court copy was a
        // draft in courtcommunication@, which was wrong whenever the court had no usable email and
        // left staff hunting through an empty Drafts folder.
        setSuccessMessage(
          [
            'Ticket approved. Client copy saved as a draft in client@.',
            data.deliverySummary,
          ]
            .filter(Boolean)
            .join(' '),
        );
      }
    } catch (err) {
      setSuccessMessage(null);
      setError(err instanceof Error ? err.message : 'Failed to approve ticket');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmLorFollowUp = async () => {
    if (!ticketGuid) return;

    try {
      setActionLoading(true);
      setError(null);
      setSuccessMessage(null);

      const response = await fetch(
        `${process.env.API_HOST || ''}/api/v1/manager/ticket/${ticketGuid}/lor-followup/confirm`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        },
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to mark LOR as confirmed');
      }

      const data: ContextTicketDto = await response.json();
      setTicket(data);
      setSuccessMessage(
        'LOR marked as confirmed. Automated follow-ups are stopped.',
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to mark LOR as confirmed',
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Rebuild the LOR from the corrected case record. Asked for by Maria Elena (Aug 5) and Hannah
  // (Aug 7): fixing the record fixed the data but nothing regenerated the letter, so the only
  // remedy was to ask a developer — or to rewrite it by hand, which is how a case ended up asking a
  // court for something it could not grant.
  //
  // Confirmed first because it puts fresh drafts in a shared mailbox that other people are working
  // out of, and the superseded ones cannot be deleted from here.
  const handleRebuildLor = async () => {
    if (!ticketGuid) return;

    // The wording splits on whether a letter already exists: warning someone about superseded
    // drafts they do not have is noise, and it hides the thing that IS true of a first letter —
    // that this is what starts the court chasing.
    const isFirstLetter = !ticket?.ticket.Ticket_LorSent;

    const confirmed = window.confirm(
      isFirstLetter
        ? 'Generate the letter of representation from the current case details?\n\n' +
            'This case has no letter yet. Drafts will be created in Outlook for review — the ' +
            'letter is not sent to the court automatically.\n\n' +
            'The Day 3 / 7 / 10 court follow-up schedule starts now.'
        : 'Rebuild the letter of representation from the current case details?\n\n' +
            'New drafts will be created in Outlook for review. The letter is not sent to the court ' +
            'automatically.\n\n' +
            'The previous drafts are NOT removed — discard them so the corrected letter is the one ' +
            'that goes out.',
    );
    if (!confirmed) return;

    try {
      setActionLoading(true);
      setError(null);
      setSuccessMessage(null);

      const response = await fetch(
        `${process.env.API_HOST || ''}/api/v1/manager/ticket/${ticketGuid}/rebuild-lor`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.message || 'Failed to rebuild the letter of representation',
        );
      }

      setTicket(data);
      // `firstLetter` comes from the server rather than the pre-click value, because the server is
      // what decides which branch ran — and it re-read the ticket after writing the LOR flags.
      setSuccessMessage(
        data.firstLetter
          ? 'Letter generated. Drafts are in Outlook for review, and the court follow-up schedule has started.'
          : 'Letter rebuilt. New drafts are in Outlook for review — discard the previous drafts so the corrected letter is the one that goes out.',
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to rebuild the letter of representation',
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestartLorFollowUp = async (stage: 1 | 2) => {
    if (!ticketGuid) return;

    try {
      setActionLoading(true);
      setError(null);
      setSuccessMessage(null);

      const response = await fetch(
        `${process.env.API_HOST || ''}/api/v1/manager/ticket/${ticketGuid}/lor-followup/restart`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ stage }),
        },
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(
          data.message || 'Failed to restart LOR follow-up automation',
        );
      }

      const data: ContextTicketDto = await response.json();
      setTicket(data);
      setSuccessMessage(
        stage === 1
          ? 'LOR follow-up automation restarted from Day 3.'
          : 'LOR follow-up automation restarted from Day 7.',
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to restart LOR follow-up automation',
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!newStatus || !statusDescription.trim()) {
      setError('Please select a status and provide a description');
      return;
    }

    try {
      setActionLoading(true);
      const response = await fetch(
        (process.env.API_HOST || '') +
          `/api/v1/manager/ticket-status/${ticketGuid}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            TicketStatus_Name: newStatus,
            TicketStatus_Desc: statusDescription,
          }),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to update ticket status');
      }

      const data = await response.json();
      setSuccessMessage('Ticket status updated successfully!');
      setNewStatus('');
      setStatusDescription('');
      fetchTicket(); // Refresh ticket data
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update ticket status',
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Offer accept/reject quick actions — record the client's decision as a status change
  // (accept → awaiting judge approval; reject → back to prosecutor negotiation).
  //
  // Confirmed since Aug 13, because these were being pressed by accident and nothing said so.
  // Fourteen "Offer accepted by client." rows exist in production and SEVEN landed within thirty
  // seconds of the case's first status row — three of them in the same second. Nobody receives,
  // reviews and accepts a prosecutor's offer that fast. One case carries three, two of them two
  // seconds apart, which is a double-click.
  //
  // Two things made it easy. The buttons render on every case whatever stage it is on, and until
  // now "Accept Offer" was a full-width green contained button — the same shape and colour as
  // "Approve Ticket" directly above it, separated by a divider. On a freshly created case those two
  // are the obvious green thing to press.
  //
  // The cost is not cosmetic. The description is client-visible (TicketView renders it), so a client
  // is told an offer was accepted on a case that never had one; and it moves the case off
  // "citation received (Approving)", which used to strand it where no LOR could be generated at all.
  //
  // Deliberately a confirmation and not a gate. Hiding these behind a stage is how the LOR dead end
  // was built in the first place — staff must still be able to record a real decision whenever it
  // arrives, including on a case whose status nobody updated.
  const handleOfferDecision = async (decision: 'accept' | 'reject') => {
    const statusName =
      decision === 'accept'
        ? 'waiting for judge to approve'
        : 'negotiating with prosecutor';
    const desc =
      decision === 'accept'
        ? 'Offer accepted by client.'
        : 'Offer rejected by client.';

    // A case with no letter of representation has almost certainly had no offer either, so this is
    // where an accidental press lands. Say so rather than asking a generic "are you sure".
    const looksPreOffer = !ticket?.ticket.Ticket_LorSent;
    const confirmed = window.confirm(
      (decision === 'accept'
        ? 'Record that the client ACCEPTED the offer?\n\n' +
          'This moves the case to "5. Offer accepted (waiting for judge to approve)" and writes ' +
          '"Offer accepted by client." to the case history, which the client can see.\n\n' +
          'It also sends the acceptance to the court (or drafts it, if automatic sending is off ' +
          'in Settings).'
        : 'Record that the client REJECTED the offer?\n\n' +
          'This moves the case back to "negotiating with prosecutor" and writes ' +
          '"Offer rejected by client." to the case history, which the client can see.') +
        (looksPreOffer
          ? '\n\n⚠️ This case has no letter of representation yet, so it is unlikely to have ' +
            'received an offer. If you meant to approve the case and send the LOR, cancel and use ' +
            'the Approve button instead.'
          : ''),
    );
    if (!confirmed) return;

    try {
      setActionLoading(true);
      const response = await fetch(
        (process.env.API_HOST || '') +
          `/api/v1/manager/ticket-status/${ticketGuid}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            TicketStatus_Name: statusName,
            TicketStatus_Desc: desc,
          }),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to record offer decision');
      }

      // Marks the offer itself and writes the case note (18 Sep meeting). The status above is
      // already saved, so a failure here is reported rather than thrown — the decision stands.
      const noted = await fetch(
        (process.env.API_HOST || '') +
          `/api/v1/manager/ticket/${ticketGuid}/offer-response`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            response: decision === 'accept' ? 'accepted' : 'rejected',
          }),
        },
      ).catch(() => null);
      // What happened with the acceptance to the court, in the server's words. Said whether or not
      // it went, because "accepted" alone would read as "the court has it".
      const notedBody = noted?.ok ? await noted.json().catch(() => null) : null;
      const acceptanceMessage: string | null = notedBody?.acceptanceDelivery?.message ?? null;

      setSuccessMessage(
        `Offer ${decision === 'accept' ? 'accepted' : 'rejected'}.` +
          (noted?.ok
            ? ''
            : ' The case note could not be written — please add one by hand.') +
          (acceptanceMessage ? ` ${acceptanceMessage}` : ''),
      );
      fetchTicket();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to record offer decision',
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusChange = (event: SelectChangeEvent) => {
    setNewStatus(event.target.value);
  };

  /**
   * Move the case by clicking a stage on the progression bar (Lloyd, Aug 19).
   *
   * The bar itself already existed on the client profile; the case page — the screen you land on
   * when you click a case — had no chart at all, which is why it read as missing. Clicking writes
   * the same status row the Update Status form writes, so nothing about history or the client-facing
   * view changes. It is confirmed because the description lands in the case history the client can
   * read, and a stray click on a bar is easier than a stray click through a form.
   */
  const handleStageSelect = async (statusName: string, stageLabel: string) => {
    const confirmed = window.confirm(
      `Move this case to "${stageLabel}"?

` +
        'This writes a status update to the case history, which the client can see.',
    );
    if (!confirmed) return;

    try {
      setActionLoading(true);
      const response = await fetch(
        (process.env.API_HOST || '') +
          `/api/v1/manager/ticket-status/${ticketGuid}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            TicketStatus_Name: statusName,
            TicketStatus_Desc: `Moved to "${stageLabel}" from the case page`,
          }),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to update case stage');
      }

      setSuccessMessage(`Case moved to "${stageLabel}".`);
      fetchTicket();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update case stage',
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditFormChange = (field: string, value: string) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveChanges = async () => {
    try {
      setActionLoading(true);
      const response = await fetch(
        (process.env.API_HOST || '') + `/api/v1/manager/ticket/${ticketGuid}`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...editForm,
            Prosecutor_guid: selectedProsecutor?.Prosecutor_guid || null,
            Court_guid: selectedCourt?.Court_guid || null,
          }),
        },
      );

      if (!response.ok) {
        /*
         * Show what the server actually said (Maria Elena, Aug 19).
         *
         * This threw a fixed string, so every possible failure — a plan that is not a product, a
         * court that could not be found, a licence mismatch, a bad date, a 500 — reached the person
         * as the same four words: "Failed to update ticket". She reported being unable to add a
         * court, and the court was not the problem; nothing on screen could have told her that.
         *
         * `parseApiErrorResponse` is already used everywhere else and unpacks all three shapes this
         * route returns ({ error }, { message }, and express-validator's { errors: [...] }).
         */
        throw new Error(
          await parseApiErrorResponse(response, 'Failed to update ticket'),
        );
      }

      const data: ContextTicketDto = await response.json();

      /*
       * The date of birth lives on the client, so it takes a second request. Three conditions have
       * to hold before it is sent, and the third is the one that matters:
       *
       *  - there is a client on this case;
       *  - this person actually changed the box;
       *  - and the stored value is still what it was when this screen loaded.
       *
       * Without the third, saving a change to the city would re-assert whatever date this screen
       * read minutes ago and overwrite a correction somebody else made in between — or clear one,
       * from a screen where the box was never opened. A save that would do that stops and says so
       * instead, because a silent overwrite is not recoverable and a message is.
       */
      const storedDob = (data.user?.User_DOB ?? '').slice(0, 10);
      const dobEdited = clientDob !== clientDobAtLoad;
      const changedElsewhere = storedDob !== clientDobAtLoad;

      let saved = data;
      let dobProblem: string | null = null;
      if (data.user?.User_guid && dobEdited && changedElsewhere) {
        dobProblem =
          'the date of birth was not saved, because somebody else changed it while this case was ' +
          'open. Reopen the case and enter it again if it is still wrong.';
      } else if (data.user?.User_guid && dobEdited) {
        try {
          await ManagerApi.updateUserDateOfBirth(data.user.User_guid, clientDob || null);
          // The case response predates the client save, so it still carries the old date.
          saved = { ...data, user: { ...data.user, User_DOB: clientDob || null } };
        } catch (err) {
          dobProblem =
            err instanceof Error ? err.message : 'the date of birth could not be saved';
        }
      }

      setTicket(saved);
      setIsEditing(false);
      // One message, not a green success beside a red failure saying opposite things.
      if (dobProblem) {
        setSuccessMessage(null);
        setError(`The case was saved, but ${dobProblem}`);
      } else {
        setSuccessMessage('Ticket updated successfully!');
        setError(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update ticket');
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Save the discovery record (Lloyd, Aug 19).
   *
   * Its own PATCH rather than a part of the big edit form: the four fields live on their own tab,
   * they are filled in weeks apart from the rest of the case, and nobody should have to put the
   * whole case into edit mode to write down that a thumb drive arrived. Takes the server's response
   * as the new case so the DS and DR boxes on the chart above move in the same render.
   */
  const handleSaveDiscovery = async (fields: {
    Ticket_DiscoveryRequestedAt: string | null;
    Ticket_DiscoveryReceivedAt: string | null;
    Ticket_DiscoveryLocation: string | null;
    Ticket_DiscoveryNote: string | null;
  }) => {
    const response = await fetch(
      (process.env.API_HOST || '') + `/api/v1/manager/ticket/${ticketGuid}`,
      {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      },
    );
    if (!response.ok) {
      throw new Error('Failed to save the discovery record');
    }
    const data: ContextTicketDto = await response.json();
    setTicket(data);
    setSuccessMessage('Discovery record saved.');
  };

  // G.6: clear a manual court-date override so the case resumes auto-populating from court events.
  /**
   * Pick the letter by hand, or hand the choice back to the rules with an empty value. Sends the
   * whole PATCH through the same endpoint the rest of the case edits use, then takes the server's
   * response as truth — the routing description has to be recomputed server-side anyway.
   */
  const handleLorOverrideChange = async (next: string) => {
    try {
      setLorOverrideSaving(true);
      const response = await fetch(
        (process.env.API_HOST || '') + `/api/v1/manager/ticket/${ticketGuid}`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ Ticket_LorTemplateOverride: next || null }),
        },
      );
      if (!response.ok) {
        throw new Error('Failed to change the letter');
      }
      const data: ContextTicketDto = await response.json();
      setTicket(data);
      setSuccessMessage(
        next
          ? `This case will now use the ${describeLorTemplate(next).toLowerCase()}. Regenerate the letter to apply it.`
          : 'Letter choice handed back to the automatic rules.',
      );
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change the letter');
    } finally {
      setLorOverrideSaving(false);
    }
  };

  const handleResetCourtDateToAuto = async () => {
    try {
      setActionLoading(true);
      const response = await fetch(
        (process.env.API_HOST || '') + `/api/v1/manager/ticket/${ticketGuid}`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ Ticket_CourtDateManualOverride: false }),
        },
      );
      if (!response.ok) {
        throw new Error('Failed to reset court date');
      }
      const data: ContextTicketDto = await response.json();
      setTicket(data);
      setSuccessMessage('Court date set back to automatic.');
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset court date');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelEdit = () => {
    if (ticket) {
      setEditForm({
        Ticket_Number: ticket.ticket.Ticket_Number,
        Ticket_Citation: ticket.ticket.Ticket_Citation,
        Ticket_Description: ticket.ticket.Ticket_Description || '',
        Ticket_OffenseDate: ticket.ticket.Ticket_OffenseDate
          ? String(ticket.ticket.Ticket_OffenseDate).slice(0, 10)
          : '',
        Ticket_CauseNumber: ticket.ticket.Ticket_CauseNumber ?? '',
        Ticket_AgencyCaseNumber: ticket.ticket.Ticket_AgencyCaseNumber ?? '',
        Ticket_LicensePlate: ticket.ticket.Ticket_LicensePlate ?? '',
        Ticket_ArrestingAgency: ticket.ticket.Ticket_ArrestingAgency ?? '',
        Ticket_ArrestingAgencyAddress:
          ticket.ticket.Ticket_ArrestingAgencyAddress ?? '',
        Ticket_RespondByDate: ticket.ticket.Ticket_RespondByDate
          ? String(ticket.ticket.Ticket_RespondByDate).slice(0, 10)
          : '',
        Ticket_JuryTrialDate: ticket.ticket.Ticket_JuryTrialDate
          ? String(ticket.ticket.Ticket_JuryTrialDate).slice(0, 10)
          : '',
        Ticket_Address: ticket.ticket.Ticket_Address,
        Ticket_City: ticket.ticket.Ticket_City,
        Ticket_County: ticket.ticket.Ticket_County,
        Ticket_LicenseType: ticket.ticket.Ticket_LicenseType,
        Ticket_Zone: ticket.ticket.Ticket_Zone,
        Ticket_CourtDate: formatDate(ticket.ticket.Ticket_CourtDate),
        Ticket_Representation: ticket.ticket.Ticket_Representation,
        Ticket_Gender: ticket.ticket.Ticket_Gender ?? '',
        Ticket_PronounSet: ticket.ticket.Ticket_PronounSet ?? '',
        Ticket_HasTakenDscInPastYear:
          ticket.ticket.Ticket_HasTakenDscInPastYear === true,
      });
      const dobOnFile = (ticket.user?.User_DOB ?? '').slice(0, 10);
      setClientDob(dobOnFile);
      setClientDobAtLoad(dobOnFile);
      setSelectedProsecutor(ticket.prosecutor || null);
      // Reset selectedCourt from prosecutor's court if available
      if (ticket.prosecutor?.court) {
        setSelectedCourt({
          ...ticket.prosecutor.court,
          Court_Active: true, // Assume active
          prosecutors: [], // Empty array since we don't have this data
        });
      } else {
        setSelectedCourt(null);
      }
    }
    setIsEditing(false);
    setError(null);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    return new Date(dateString).toISOString().split('T')[0];
  };

  const getStatusColor = (status: string) => {
    return '';
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error && !ticket) {
    return (
      <>
        <Box p={3}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button onClick={() => navigate(-1)} variant="outlined">
            Back
          </Button>
          <br />
        </Box>
      </>
    );
  }

  if (!ticket) {
    return (
      <>
        <Box p={3}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Ticket not found
          </Alert>
          <Button onClick={() => navigate(-1)} variant="outlined">
            Back
          </Button>
          <br />
        </Box>
      </>
    );
  }

  const isApprovingStatus =
    ticket.ticketStatus?.TicketStatus_Name ===
      'citation received (Approving)' ||
    (ticket.ticketStatus?.TicketStatus_Name as string) === 'approving';

  const approvalBlockReason = getTicketApprovalBlockReason(
    ticket.ticket.Ticket_Number,
    ticket.ticket.Ticket_MatterType,
  );

  // Classified by the server (ContextTicketDto.courtEmailStatus) so there is one definition of an
  // unusable court address. Deliberately a warning and not a block: several hundred courts are
  // genuinely fax- or mail-only, and blocking those would train staff to click past the message.
  // A court recorded as taking letters by fax, post or its website is not emailed the letter even
  // when its mailbox is on file (LorCourtDeliveryService.courtTakesLetterElsewhere). Checked first,
  // since that is the reason the copy goes internal whatever the address says.
  const courtFilingMethod = ticket.court?.Court_FilingMethod;
  const courtEmailWarning =
    courtFilingMethod && courtFilingMethod !== 'email'
      ? `This court takes letters ${
          courtFilingMethod === 'website'
            ? 'on its website'
            : courtFilingMethod === 'fax'
              ? 'by fax'
              : 'by post'
        }. The court copy will go to the internal LOR mailbox so it can be sent that way.`
      : ticket.courtEmailStatus === 'missing'
      ? 'This court has no email address on file. The court copy will go to the internal LOR mailbox instead of the court.'
      : ticket.courtEmailStatus === 'placeholder'
        ? "This court's email address is a placeholder. The court copy will go to the internal LOR mailbox instead of the court."
        : null;

  const lorFollowUpActive =
    Boolean(ticket.ticket.Ticket_LorSent) &&
    !ticket.ticket.Ticket_LorConfirmedAt;
  const lorFollowUpConfirmed = Boolean(ticket.ticket.Ticket_LorConfirmedAt);
  const lorFollowUpStarted = Boolean(ticket.ticket.Ticket_LorSent);

  // Same requirement the server enforces on the rebuild route. Surfaced here so the reason is on
  // screen before the click, rather than arriving as a 400 after it. Only consulted inside the
  // traffic-matter branch, so it does not need the non-traffic exemption approval's check carries.
  const lorBlockReason = ticket.ticket.Ticket_Number?.trim()
    ? null
    : 'A ticket number must be entered before the letter of representation can be generated.';

  return (
    <>
      <Box sx={{ position: 'relative', minHeight: '100vh' }}>
        <Box className="manager-ticket-view">
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          {approvalWarnings.map((warning) => (
            <Alert key={warning} severity="warning" sx={{ mb: 2 }}>
              {warning}
            </Alert>
          ))}

          <Button
            onClick={() => navigate(-1)}
            variant="outlined"
            className="back-button"
          >
            ← Back
          </Button>
          <br />
          <br />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h4" gutterBottom>
              Ticket Details
            </Typography>
            <Button
              color="error"
              variant="outlined"
              size="small"
              onClick={() => setDeleteCaseOpen(true)}
            >
              Delete Case
            </Button>
          </Box>

          {/* Delete Case Confirmation (soft delete) */}
          <Dialog open={deleteCaseOpen} onClose={() => { if (!deletingCase) setDeleteCaseOpen(false); }}>
            <DialogTitle>Delete Case</DialogTitle>
            <DialogContent>
              <Typography variant="body2">
                Delete this case? It will be removed from all lists. Payment history and notes are
                preserved for the record, and the case can be restored later if needed.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteCaseOpen(false)} disabled={deletingCase}>
                Cancel
              </Button>
              <Button onClick={handleDeleteCase} color="error" variant="contained" disabled={deletingCase}>
                {deletingCase ? 'Deleting...' : 'Delete Case'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Client (name links to the full profile; contact details are actionable). */}
          {ticket.user && (
            <Box mb={3} sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="h5">
                <Link
                  to={`/user?User_guid=${ticket.user.User_guid}`}
                  style={{
                    textDecoration: 'none',
                    color: 'inherit',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  {ticket.user.User_FirstName} {ticket.user.User_LastName}
                  <LinkIcon fontSize="small" sx={{ opacity: 0.7 }} />
                </Link>
              </Typography>
              {ticket.user.User_Email && (
                <MuiLink href={`mailto:${ticket.user.User_Email}`} underline="hover" variant="body2">
                  {ticket.user.User_Email}
                </MuiLink>
              )}
              {ticket.user.User_PhoneNumber && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <MuiLink href={`tel:${ticket.user.User_PhoneNumber}`} underline="hover" variant="body2">
                    {ticket.user.User_PhoneNumber}
                  </MuiLink>
                  <MuiLink href={`sms:${ticket.user.User_PhoneNumber}`} underline="hover" variant="body2">
                    Text
                  </MuiLink>
                </Box>
              )}
              <Button variant="outlined" size="small" onClick={() => setSendUpdateOpen(true)}>
                Send update
              </Button>
              <SendUpdateDialog
                open={sendUpdateOpen}
                onClose={() => setSendUpdateOpen(false)}
                client={{
                  guid: ticket.user.User_guid,
                  name: `${ticket.user.User_FirstName ?? ''} ${ticket.user.User_LastName ?? ''}`.trim(),
                }}
              />
            </Box>
          )}

          {/*
            The case tabs: Status History, Court Events, Documents and the rest. At the top, under the
            client, since 14 Oct 2026 (Lloyd: "Towards the bottom, you have a list of options ... Can
            you move this to the top, so no one has to scroll down looking for it?"). They sat below
            about fifteen hundred lines of detail cards.
          */}
          <Box mb={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  aria-label="ticket information tabs"
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  {/* Real links, so right-click, Ctrl-click or middle-click opens the tab in a new
                      browser tab. A plain click still switches in place without reloading. */}
                  {CASE_TABS.map((tab) => (
                    <Tab
                      key={tab.slug}
                      label={tab.label}
                      component="a"
                      href={caseTabHref(tab.slug)}
                      onClick={(event: React.MouseEvent) => {
                        if (!event.ctrlKey && !event.metaKey && !event.shiftKey && event.button === 0) {
                          event.preventDefault();
                        }
                      }}
                    />
                  ))}
                </Tabs>
                {/* Lloyd, 14 Oct 2026: "the one document I, as the attorney, need to see more often
                    than anything else is the citation. Can you make it so that I can click on the
                    citation from pretty much every page within the case file?" Beside the tabs, so
                    it is there whichever tab is open. */}
                {ticket.user?.User_guid && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setCitationOpen(true)}
                    sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                  >
                    View citation
                  </Button>
                )}
                </Box>

                <TabPanel value={activeTab} index={tabIndex('history')}>
                  {/*
                    Sits with the status history because that is exactly what the link exposes —
                    progression, current status and outcome. Keeping it beside the thing it shares
                    makes the scope obvious to whoever is about to send it.
                  */}
                  {ticket?.ticket?.Ticket_guid && (
                    <Box sx={{ mb: 3 }}>
                      <CaseShareLinkPanel Ticket_guid={ticket.ticket.Ticket_guid} />
                    </Box>
                  )}
                  {ticket.statusHistory && ticket.statusHistory.length > 0 ? (
                    <Stack spacing={2}>
                      {ticket.statusHistory.map((status, index) => (
                        <Box
                          key={status.TicketStatus_guid}
                          sx={{
                            p: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            backgroundColor:
                              index === 0 ? 'action.hover' : 'transparent',
                          }}
                        >
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            alignItems={{ sm: 'center' }}
                          >
                            <Box flex={1}>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                mb={1}
                              >
                                <Chip
                                  label={status.TicketStatus_Name}
                                  color={
                                    getStatusColor(
                                      status.TicketStatus_Name,
                                    ) as any
                                  }
                                  size="small"
                                />
                                {index === 0 && (
                                  <Chip
                                    label="Current"
                                    color="primary"
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </Stack>
                              <Typography variant="body2" color="text.primary">
                                {status.TicketStatus_Desc}
                              </Typography>
                            </Box>
                            <Box minWidth="120px">
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {formatDate(status.TicketStatus_DateTime)}
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No status history available.
                    </Typography>
                  )}
                </TabPanel>

                <TabPanel value={activeTab} index={tabIndex('court-events')}>
                  <ManagerTicketsList
                    courtEvents={courtEvents.map((event) => ({
                      ...event,
                      ticketNumber: ticket?.ticket?.Ticket_Number || '',
                      ticketGuid: ticket?.ticket?.Ticket_guid || '',
                    }))}
                    courtEventsLoading={courtEventsLoading}
                    courtEventError={courtEventError}
                    actionLoading={actionLoading}
                    onCreateEvent={handleCreateEvent}
                    onEditEvent={handleEditEvent}
                    onDeleteEvent={handleDeleteEvent}
                    currentTicket={
                      ticket?.ticket
                        ? {
                            Ticket_guid: ticket.ticket.Ticket_guid,
                            Ticket_Number: ticket.ticket.Ticket_Number,
                          }
                        : undefined
                    }
                    onCreateEventWithFile={handleCreateEventWithFile}
                    uploadingFile={uploadingFile}
                    // Reload so the "confirmed by" stamp reflects who just answered, rather than
                    // showing the new value still labelled unconfirmed.
                    onAppearanceChanged={() => loadCourtEvents()}
                    highlightedCourtEventGuid={highlightedCourtEventGuid}
                    coverageByEvent={coverageByEvent}
                    coveringAttorneys={coverageLoadFailed ? undefined : coveringAttorneys}
                    coverageCourtGuid={ticket?.court?.Court_guid ?? null}
                    onCoverageRecorded={() => loadCoverage()}
                  />
                </TabPanel>

                <TabPanel value={activeTab} index={tabIndex('documents')}>
                  {ticket?.ticket?.Ticket_guid && (
                    <AlprReadinessBanner Ticket_guid={ticket.ticket.Ticket_guid} />
                  )}
                  {/*
                    Uploading comes first (Hannah, 15 Sep 2026). Everything below this generates a
                    document; this is the only panel that takes one IN, and it used to sit under all
                    nine of them, so attaching a court notice meant scrolling past the whole tab.
                    Reading the case's documents is also the commoner errand of the two.
                  */}
                  {ticket?.user?.User_guid && ticket?.ticket?.Ticket_guid ? (
                    <CaseDocumentsPanel
                      User_guid={ticket.user.User_guid}
                      Ticket_guid={ticket.ticket.Ticket_guid}
                      citationDocumentGuid={ticket.ticket.Ticket_Url || undefined}
                      onCitationChanged={fetchTicket}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Documents are unavailable for this case.
                    </Typography>
                  )}
                  {/*
                    The bond generator sits above the document list because what it produces IS a
                    case document — and because whoever is looking for "the bond" comes here first.
                    It renders its own blockers, so it is safe to show on every case; a case that is
                    nowhere near an appeal simply says what is missing.
                  */}
                  {ticket?.ticket?.Ticket_guid && (
                    <AppealBondPanel Ticket_guid={ticket.ticket.Ticket_guid} />
                  )}
                  {/* The other bond the firm generates. Its amount is whatever the court set, so it
                      asks for one rather than computing it from the fine. */}
                  {ticket?.ticket?.Ticket_guid && (
                    <AttorneyBondPanel Ticket_guid={ticket.ticket.Ticket_guid} />
                  )}
                  {/* Records a bond that already exists, and shows the court level + FTA status it
                      implies — the two facts the failure-to-appear fee hangs on (Lloyd, Aug 24). */}
                  {ticket?.ticket?.Ticket_guid && (
                    <BondRecordPanel Ticket_guid={ticket.ticket.Ticket_guid} />
                  )}
                  {/* Reads the two facts BondRecordPanel above records, and turns them into the
                      number. Read-only: it explains a fee, it never charges one. */}
                  {ticket?.ticket?.Ticket_guid && (
                    <FailureToAppearFeePanel Ticket_guid={ticket.ticket.Ticket_guid} />
                  )}
                  {/* The conditions record the closing letter is generated from (Lloyd, Aug 21). */}
                  {ticket?.ticket?.Ticket_guid && (
                    <CaseConditionsPanel Ticket_guid={ticket.ticket.Ticket_guid} />
                  )}
                  {/*
                    Last, because it is the end of the case, and it reads downward from the two
                    panels above it: the conditions it prints are entered directly above, and its
                    other refusal — a live surety bond — is cleared two panels up.
                  */}
                  {ticket?.ticket?.Ticket_guid && (
                    <ClosingLetterPanel Ticket_guid={ticket.ticket.Ticket_guid} />
                  )}
                </TabPanel>

                {/*
                  Motions (Lloyd, 6 Oct 2026: "could you move all of the motions to its own tab?").
                  The five filings that ask the court for something, moved here from Documents in
                  the order they had there. The bonds stay in Documents: they are not motions.
                */}
                <TabPanel value={activeTab} index={tabIndex('motions')}>
                  {/*
                    In FRONT of the motion to reset, which is what Lloyd asked for by name: "this
                    should be a button, similar to and in front of the motion to reset". The two are
                    the pair a client's "do I have to be there?" splits into, so they sit together.
                  */}
                  {ticket?.ticket?.Ticket_guid && (
                    <RemoteAppearancePanel
                      Ticket_guid={ticket.ticket.Ticket_guid}
                      courtName={ticket.court?.Court_Name ?? null}
                    />
                  )}
                  {ticket?.ticket?.Ticket_guid && (
                    <ContinuanceMotionPanel Ticket_guid={ticket.ticket.Ticket_guid} />
                  )}
                  {/* Beside the continuance motion, because they are the two answers to the same
                      question from a client: move the setting, or do not make me attend it. */}
                  {ticket?.ticket?.Ticket_guid && (
                    <AbsenceMotionPanel
                      Ticket_guid={ticket.ticket.Ticket_guid}
                      courtName={ticket.court?.Court_Name ?? null}
                    />
                  )}
                  {/* After the settings motions and before the surety one, because those two ask
                      the court to move a date while this one ends the representation. Kelsi asked
                      for it twice: the only withdrawal here was the surety bond, which is rare. */}
                  {ticket?.ticket?.Ticket_guid && (
                    <WithdrawalMotionPanel Ticket_guid={ticket.ticket.Ticket_guid} />
                  )}
                  {/*
                    Directly under the bond generator, because it is the other end of the same
                    story: one panel puts the firm on a bond, this one takes it off. It renders its
                    own state, so a case with no bond simply says there is nothing to withdraw from.
                  */}
                  {ticket?.ticket?.Ticket_guid && (
                    <SuretyWithdrawalPanel Ticket_guid={ticket.ticket.Ticket_guid} />
                  )}
                </TabPanel>

                {/*
                  Discovery (Lloyd, Aug 19): "a tab button for discovery requested and discovery
                  received and location". The location is the part that costs time — what comes back
                  is usually dashcam video on physical media, and "waiting to be picked up from
                  court" is an errand, not a filing.
                */}
                <TabPanel value={activeTab} index={tabIndex('discovery')}>
                  <CaseDiscoveryPanel
                    value={{
                      Ticket_DiscoveryRequestedAt:
                        ticket.ticket.Ticket_DiscoveryRequestedAt,
                      Ticket_DiscoveryReceivedAt:
                        ticket.ticket.Ticket_DiscoveryReceivedAt,
                      Ticket_DiscoveryLocation:
                        ticket.ticket.Ticket_DiscoveryLocation,
                      Ticket_DiscoveryNote: ticket.ticket.Ticket_DiscoveryNote,
                    }}
                    onSave={handleSaveDiscovery}
                    disabled={actionLoading}
                  />
                  {ticket?.ticket?.Ticket_guid && (
                    <CaseDiscoveryRequestsPanel
                      Ticket_guid={ticket.ticket.Ticket_guid}
                    />
                  )}
                </TabPanel>

                <TabPanel value={activeTab} index={tabIndex('payments')}>
                  {ticket?.ticket?.Ticket_guid ? (
                    <CasePaymentsPanel Ticket_guid={ticket.ticket.Ticket_guid} />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Payments are unavailable for this case.
                    </Typography>
                  )}
                </TabPanel>

                <TabPanel value={activeTab} index={tabIndex('offer')}>
                  {ticket?.ticket?.Ticket_guid ? (
                    <>
                      <CaseOfferPanel
                        Ticket_guid={ticket.ticket.Ticket_guid}
                        onStageMoved={fetchTicket}
                      />
                      <RejectOfferPanel Ticket_guid={ticket.ticket.Ticket_guid} />
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Offer tools are unavailable for this case.
                    </Typography>
                  )}
                </TabPanel>

                {/*
                  Everything on the case in one list (Lloyd, 6 Oct 2026). The client's messages are
                  kept per client, not per case, so a client with two cases sees the same messages
                  on both.
                */}
                <TabPanel value={activeTab} index={tabIndex('activity')}>
                  {ticket?.ticket?.Ticket_guid ? (
                    <CaseActivityPanel
                      Ticket_guid={ticket.ticket.Ticket_guid}
                      User_guid={ticket.user?.User_guid}
                      clientName={ticket.user ? clientDisplayName(ticket.user) : undefined}
                      caseNumber={ticket.ticket.Ticket_MatterNumber || ticket.ticket.Ticket_Number || undefined}
                      onOpenOffer={() => selectTab(tabIndex('offer'))}
                      onStageMoved={fetchTicket}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Activity is unavailable for this case.
                    </Typography>
                  )}
                </TabPanel>

                <TabPanel value={activeTab} index={tabIndex('replies')}>
                  {ticket?.ticket?.Ticket_guid ? (
                    <CaseReplyDraftsPanel Ticket_guid={ticket.ticket.Ticket_guid} />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Replies are unavailable for this case.
                    </Typography>
                  )}
                </TabPanel>

              </CardContent>
            </Card>

            {/* Court Event Dialog */}
            <Dialog
              open={showEventDialog}
              onClose={
                uploadingFile ? undefined : () => setShowEventDialog(false)
              }
              maxWidth="sm"
              fullWidth
              disableEscapeKeyDown={uploadingFile}
            >
              <DialogTitle>
                {editingEvent ? 'Edit Court Event' : 'Create Court Event'}
                {uploadingFile && (
                  <Typography
                    variant="caption"
                    color="primary"
                    sx={{ display: 'block', mt: 1 }}
                  >
                    Processing document... Please wait.
                  </Typography>
                )}
              </DialogTitle>
              <DialogContent>
                <Stack spacing={3} sx={{ mt: 1 }}>
                  {/* File Upload Section - only show when creating new event */}
                  {!editingEvent && (
                    <Accordion
                      variant="outlined"
                      sx={{
                        backgroundColor: uploadingFile
                          ? 'action.hover'
                          : 'background.paper',
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        aria-controls="file-upload-content"
                        id="file-upload-header"
                      >
                        <Typography variant="subtitle1" color="primary">
                          📄 Upload Document (Optional)
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack spacing={2}>
                          <Typography variant="body2" color="text.secondary">
                            Upload a court document to automatically extract
                            dates and times
                          </Typography>

                          {uploadingFile ? (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                p: 2,
                                backgroundColor: 'primary.light',
                                borderRadius: 1,
                              }}
                            >
                              <CircularProgress size={20} />
                              <Typography
                                variant="body2"
                                color="primary.contrastText"
                              >
                                Processing {selectedFile?.name}...
                              </Typography>
                            </Box>
                          ) : (
                            <Button
                              variant="outlined"
                              component="label"
                              disabled={uploadingFile}
                              fullWidth
                            >
                              Choose File
                              <input
                                type="file"
                                hidden
                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    handleFileUpload(file);
                                  }
                                }}
                              />
                            </Button>
                          )}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  )}
                  <Typography variant="subtitle1" color="primary">
                    Start Date & Time
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Start Date"
                      type="date"
                      value={eventForm.CourtEvent_StartDate}
                      onChange={(e) => {
                        const newStartDate = e.target.value;
                        setEventForm({
                          ...eventForm,
                          CourtEvent_StartDate: newStartDate,
                          // Auto-populate end date if it's empty
                          CourtEvent_EndDate:
                            eventForm.CourtEvent_EndDate || newStartDate,
                        });
                      }}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      required
                      disabled={uploadingFile}
                      error={eventForm.CourtEvent_StartDate.trim() === ''}
                      helperText={
                        eventForm.CourtEvent_StartDate.trim() === ''
                          ? 'Start date is required'
                          : ''
                      }
                    />
                    <TextField
                      label="Start Time"
                      type="time"
                      value={eventForm.CourtEvent_StartTime}
                      onChange={(e) => {
                        const newStartTime = e.target.value;
                        setEventForm({
                          ...eventForm,
                          CourtEvent_StartTime: newStartTime,
                          // Auto-populate end time if it's empty (add 1 hour)
                          CourtEvent_EndTime:
                            eventForm.CourtEvent_EndTime ||
                            (() => {
                              if (newStartTime) {
                                const [hours, minutes] =
                                  newStartTime.split(':');
                                const endHour = (parseInt(hours) + 1) % 24;
                                return `${endHour.toString().padStart(2, '0')}:${minutes}`;
                              }
                              return '';
                            })(),
                        });
                      }}
                      slotProps={{ htmlInput: { step: 900 } }}
                      inputProps={{ step: 900 }}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      required
                      disabled={uploadingFile}
                      error={eventForm.CourtEvent_StartTime.trim() === ''}
                      helperText={
                        eventForm.CourtEvent_StartTime.trim() === ''
                          ? 'Start time is required'
                          : ''
                      }
                    />
                  </Stack>

                  <Typography variant="subtitle1" color="primary">
                    End Date & Time
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="End Date"
                      type="date"
                      value={eventForm.CourtEvent_EndDate}
                      onChange={(e) =>
                        setEventForm({
                          ...eventForm,
                          CourtEvent_EndDate: e.target.value,
                        })
                      }
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      required
                      disabled={uploadingFile}
                      error={
                        eventForm.CourtEvent_EndDate.trim() === '' ||
                        !isEndTimeAfterStartTime()
                      }
                      helperText={
                        eventForm.CourtEvent_EndDate.trim() === ''
                          ? 'End date is required'
                          : !isEndTimeAfterStartTime()
                            ? 'End date/time must be after start date/time'
                            : ''
                      }
                    />
                    <TextField
                      label="End Time"
                      type="time"
                      value={eventForm.CourtEvent_EndTime}
                      onChange={(e) =>
                        setEventForm({
                          ...eventForm,
                          CourtEvent_EndTime: e.target.value,
                        })
                      }
                      slotProps={{ htmlInput: { step: 900 } }}
                      inputProps={{ step: 900 }}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      required
                      disabled={uploadingFile}
                      error={
                        eventForm.CourtEvent_EndTime.trim() === '' ||
                        !isEndTimeAfterStartTime()
                      }
                      helperText={
                        eventForm.CourtEvent_EndTime.trim() === ''
                          ? 'End time is required'
                          : !isEndTimeAfterStartTime()
                            ? 'End date/time must be after start date/time'
                            : ''
                      }
                    />
                  </Stack>

                  <TextField
                    label="Title"
                    value={eventForm.CourtEvent_Title}
                    onChange={(e) =>
                      setEventForm({
                        ...eventForm,
                        CourtEvent_Title: e.target.value,
                      })
                    }
                    fullWidth
                    placeholder="Enter event title..."
                    required
                    disabled={uploadingFile}
                    error={eventForm.CourtEvent_Title.trim() === ''}
                    helperText={
                      eventForm.CourtEvent_Title.trim() === ''
                        ? 'Title is required'
                        : ''
                    }
                  />

                  <TextField
                    label="Description (optional)"
                    multiline
                    rows={3}
                    value={eventForm.CourtEvent_Desc}
                    onChange={(e) =>
                      setEventForm({
                        ...eventForm,
                        CourtEvent_Desc: e.target.value,
                      })
                    }
                    fullWidth
                    placeholder="Enter event description..."
                    disabled={uploadingFile}
                  />

                  {!editingEvent && noticeAppearance && (
                    <FormControl fullWidth>
                      <InputLabel id="notice-appearance-label">Client appearance</InputLabel>
                      <Select
                        labelId="notice-appearance-label"
                        label="Client appearance"
                        value={noticeAppearance.requirement}
                        onChange={(e) =>
                          setNoticeAppearance({
                            ...noticeAppearance,
                            requirement: e.target.value as AppearanceRequirement,
                          })
                        }
                        disabled={uploadingFile}
                      >
                        {Object.entries(APPEARANCE_LABELS).map(([key, label]) => (
                          <MenuItem key={key} value={key}>
                            {label}
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        {noticeAppearance.quote
                          ? `The notice said: "${noticeAppearance.quote}"`
                          : 'The notice said the client must attend.'}{' '}
                        Check it against the notice before saving.
                      </Typography>
                    </FormControl>
                  )}
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setShowEventDialog(false)}
                  disabled={actionLoading || uploadingFile}
                >
                  Cancel
                </Button>
                <Tooltip
                  title={
                    uploadingFile
                      ? 'Please wait for document processing to complete'
                      : !isCourtEventFormComplete()
                        ? 'Please fill in all required fields'
                        : !isEndTimeAfterStartTime()
                          ? 'End date and time must be after start date and time'
                          : ''
                  }
                  placement="top"
                >
                  <span>
                    <Button
                      onClick={handleSaveEvent}
                      variant="contained"
                      disabled={
                        actionLoading ||
                        uploadingFile ||
                        !isCourtEventFormValid()
                      }
                    >
                      {actionLoading ? (
                        <CircularProgress size={20} />
                      ) : editingEvent ? (
                        'Update'
                      ) : (
                        'Create'
                      )}
                    </Button>
                  </span>
                </Tooltip>
              </DialogActions>
            </Dialog>
          </Box>

          {ticket.user?.User_guid && ticket.ticket?.Ticket_guid && (
            <Dialog open={citationOpen} onClose={() => setCitationOpen(false)} maxWidth="lg" fullWidth>
              <DialogContent>
                <CaseCitationCard
                  User_guid={ticket.user.User_guid}
                  Ticket_guid={ticket.ticket.Ticket_guid}
                  Ticket_Url={ticket.ticket.Ticket_Url}
                  onOpenDocuments={() => {
                    setCitationOpen(false);
                    selectTab(tabIndex('documents'));
                  }}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setCitationOpen(false)}>Close</Button>
              </DialogActions>
            </Dialog>
          )}

          {/*
            Case progression (Lloyd, Aug 19: "the chart is missing on new cases").

            It was never missing — it had only ever been built into the client profile and the
            client-facing progress page, and not into the case page you reach by clicking a case.
            Full width and above the detail cards, because the first question on opening a case is
            where it stands.
          */}
          <Box mb={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Case Progression
                </Typography>
                <CaseStatusTimeline
                  statusName={ticket.ticketStatus?.TicketStatus_Name}
                  matterType={ticket.ticket.Ticket_MatterType}
                  currentStageIndex={deriveStageIndex(
                    ticket.ticket,
                    ticket.ticketStatus?.TicketStatus_Name,
                    hasFutureCourtEvent,
                  )}
                  onSelectStage={handleStageSelect}
                  disabled={actionLoading}
                  showOfferDetail
                  ticketGuid={ticket.ticket.Ticket_guid}
                  /*
                   * The two discovery facts stay computed from the ticket we already have, so the
                   * chart is never wrong while the extra facts are still loading or if that request
                   * fails. The other six need offers, conditions and bonds, which the case page
                   * does not load, so they come from /journey-facts.
                   */
                  facts={{
                    discoveryRequested: Boolean(
                      ticket.ticket.Ticket_DiscoveryRequestedAt,
                    ),
                    discoveryReceived: Boolean(
                      ticket.ticket.Ticket_DiscoveryReceivedAt,
                    ),
                    ...journeyFacts,
                  }}
                  stamps={journeyStamps}
                  onSaveStamp={saveJourneyStamp}
                />
                <CountyCasePanel
                  ticketGuid={ticket.ticket.Ticket_guid}
                  countyCase={countyCase}
                  appealedFrom={appealedFrom}
                  hasAppealBond={Boolean(journeyFacts.appealBondCreated)}
                  appealFee={appealFee}
                  onOpened={() => {
                    setSuccessMessage(
                      'County case opened and linked to this one. Nothing was sent to the court or the client.',
                    );
                    loadJourney(ticket.ticket.Ticket_guid);
                  }}
                  onAppealFeeChanged={() => loadJourney(ticket.ticket.Ticket_guid)}
                />
              </CardContent>
            </Card>
          </Box>

          {/* Approved contacts on this case: an OTR case manager and the like (Lloyd, 9 Oct 2026). */}
          {ticket.user?.User_guid && ticket.ticket?.Ticket_guid && (
            <ApprovedContactsPanel
              userGuid={ticket.user.User_guid}
              ticketGuid={ticket.ticket.Ticket_guid}
              cases={[
                {
                  guid: ticket.ticket.Ticket_guid,
                  label: `This case (${ticket.ticket.Ticket_Number?.trim() || 'no citation number'})`,
                },
              ]}
              clientName={clientDisplayName(ticket.user)}
            />
          )}

          {/*
            The citation itself (Lloyd, Aug 19): "in reality, if the citation is readily visible,
            that has all the information I really ever need." Directly under the progression chart
            and above the detail, which is the order he described — where the case stands, then the
            piece of paper it is about.
          */}
          {ticket.user?.User_guid && ticket.ticket?.Ticket_guid && (
            <Box mb={3}>
              <CaseCitationCard
                User_guid={ticket.user.User_guid}
                Ticket_guid={ticket.ticket.Ticket_guid}
                Ticket_Url={ticket.ticket.Ticket_Url}
                onOpenDocuments={() => selectTab(2)}
              />
            </Box>
          )}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            {/* Ticket Information */}
            <Box flex={2}>
              <Card>
                <CardContent>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography variant="h6">Ticket Information</Typography>
                    {!isEditing ? (
                      <Stack direction="row" spacing={2} alignItems="center">
                        <MuiLink
                          component="button"
                          type="button"
                          variant="caption"
                          underline="hover"
                          onClick={() => setShowCaseData((open) => !open)}
                        >
                          {showCaseData
                            ? 'Hide the rest of the case data'
                            : 'Show the rest of the case data'}
                        </MuiLink>
                        <IconButton
                          onClick={() => {
                            setIsEditing(true);
                            setShowCaseData(true);
                          }}
                          size="small"
                        >
                          <Edit />
                        </IconButton>
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          onClick={handleSaveChanges}
                          size="small"
                          color="primary"
                          disabled={actionLoading}
                        >
                          <Save />
                        </IconButton>
                        <IconButton
                          onClick={handleCancelEdit}
                          size="small"
                          color="secondary"
                        >
                          <Cancel />
                        </IconButton>
                      </Stack>
                    )}
                  </Stack>

                  <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <Box flex={1}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Ticket Number
                        </Typography>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={editForm.Ticket_Number}
                            onChange={(e) =>
                              handleEditFormChange(
                                'Ticket_Number',
                                e.target.value,
                              )
                            }
                          />
                        ) : (
                          <Typography variant="body1" gutterBottom>
                            {ticket.ticket.Ticket_Number}
                          </Typography>
                        )}
                      </Box>

                      <Box flex={1}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Citation
                        </Typography>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={editForm.Ticket_Citation}
                            onChange={(e) =>
                              handleEditFormChange(
                                'Ticket_Citation',
                                e.target.value,
                              )
                            }
                          />
                        ) : (
                          <Typography variant="body1" gutterBottom>
                            {ticket.ticket.Ticket_Citation}
                          </Typography>
                        )}
                      </Box>

                      <Box flex={1}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Description
                        </Typography>
                        {isEditing ? (
                          <FormControl fullWidth size="small">
                            <Select
                              value={editForm.Ticket_Description}
                              displayEmpty
                              onChange={(e) =>
                                handleEditFormChange('Ticket_Description', e.target.value)
                              }
                            >
                              <MenuItem value=""><em>Not specified</em></MenuItem>
                              {OFFENSE_DESCRIPTION_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Typography variant="body1" gutterBottom>
                            {ticket.ticket.Ticket_Description || '—'}
                          </Typography>
                        )}
                      </Box>

                      {/*
                        Offense date drives the "older than two years → not-guilty letter" rule.
                        Without it the rule falls back to the court date, which a later setting can
                        move — so this is worth filling in on old cases even though it is optional.
                      */}
                      <Box flex={1}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Offense date
                        </Typography>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            size="small"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            helperText="When the citation was issued"
                            value={editForm.Ticket_OffenseDate}
                            onChange={(e) =>
                              handleEditFormChange('Ticket_OffenseDate', e.target.value)
                            }
                          />
                        ) : (
                          <Typography variant="body1" gutterBottom>
                            {ticket.ticket.Ticket_OffenseDate
                              ? new Date(ticket.ticket.Ticket_OffenseDate).toLocaleDateString('en-US')
                              : '—'}
                          </Typography>
                        )}
                      </Box>

                      <Box flex={1}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Respond-by date
                        </Typography>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            size="small"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={editForm.Ticket_RespondByDate}
                            onChange={(e) =>
                              handleEditFormChange('Ticket_RespondByDate', e.target.value)
                            }
                          />
                        ) : (
                          <Typography variant="body1" gutterBottom>
                            {ticket.ticket.Ticket_RespondByDate
                              ? new Date(ticket.ticket.Ticket_RespondByDate).toLocaleDateString('en-US')
                              : '—'}
                          </Typography>
                        )}
                      </Box>

                      <Box flex={1}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Jury trial date
                        </Typography>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            size="small"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={editForm.Ticket_JuryTrialDate}
                            onChange={(e) =>
                              handleEditFormChange('Ticket_JuryTrialDate', e.target.value)
                            }
                          />
                        ) : (
                          <Typography variant="body1" gutterBottom>
                            {ticket.ticket.Ticket_JuryTrialDate
                              ? new Date(ticket.ticket.Ticket_JuryTrialDate).toLocaleDateString('en-US')
                              : '—'}
                          </Typography>
                        )}
                      </Box>
                    </Stack>

                    {/*
                      Everything from here to the court date is the detail Lloyd asked to stop
                      seeing on every case (Aug 19): "I don't think we need any of the following:
                      the agency case number; license plate, arresting agency; arresting agency
                      address; city, county; court address; gender, DL, representation plan
                      (maybe?), zone, etc. ... can all of that be hidden or in a separate tab?"

                      Hidden rather than moved to a tab, and hidden rather than removed. Dani fills
                      several of these in, the ALPR letters print four of them, and the LOR routing
                      reads the licence type — so they stay on the same form, behind one toggle,
                      and are edited in the same place they always were.
                    */}
                    <Collapse in={showCaseData} unmountOnExit>
                      <Stack spacing={2}>
                        {/*
                          These five feed the two documents that go out with a not-guilty letter — the
                          39.14 ALPR request and the preservation demand (Lloyd, Aug 17 2026). Every one
                          is optional: anything left empty prints as a ruled blank on the documents for
                          Lloyd to complete by hand, so a case is never blocked on them. Filling them in
                          is what makes the letters go out complete.
                        */}
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Not-guilty attachments
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Printed on the 39.14 ALPR request and the preservation demand. Blank fields
                            print as a ruled blank line on those documents.
                          </Typography>
                        </Box>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <Box flex={1}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Cause number
                            </Typography>
                            {isEditing ? (
                              <TextField
                                fullWidth
                                size="small"
                                helperText="The court's cause no., not the citation number"
                                value={editForm.Ticket_CauseNumber}
                                onChange={(e) =>
                                  handleEditFormChange('Ticket_CauseNumber', e.target.value)
                                }
                              />
                            ) : (
                              <Typography variant="body1" gutterBottom>
                                {ticket.ticket.Ticket_CauseNumber || '—'}
                              </Typography>
                            )}
                          </Box>

                          <Box flex={1}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Agency case number
                            </Typography>
                            {isEditing ? (
                              <TextField
                                fullWidth
                                size="small"
                                helperText="The police report number"
                                value={editForm.Ticket_AgencyCaseNumber}
                                onChange={(e) =>
                                  handleEditFormChange('Ticket_AgencyCaseNumber', e.target.value)
                                }
                              />
                            ) : (
                              <Typography variant="body1" gutterBottom>
                                {ticket.ticket.Ticket_AgencyCaseNumber || '—'}
                              </Typography>
                            )}
                          </Box>

                          <Box flex={1}>
                            <Typography variant="subtitle2" color="text.secondary">
                              License plate
                            </Typography>
                            {isEditing ? (
                              <TextField
                                fullWidth
                                size="small"
                                helperText="Plate the ALPR records are pulled by"
                                value={editForm.Ticket_LicensePlate}
                                onChange={(e) =>
                                  handleEditFormChange('Ticket_LicensePlate', e.target.value)
                                }
                              />
                            ) : (
                              <Typography variant="body1" gutterBottom>
                                {ticket.ticket.Ticket_LicensePlate || '—'}
                              </Typography>
                            )}
                          </Box>
                        </Stack>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <Box flex={1}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Arresting agency
                            </Typography>
                            {isEditing ? (
                              <TextField
                                fullWidth
                                size="small"
                                helperText="Who the preservation demand is addressed to"
                                value={editForm.Ticket_ArrestingAgency}
                                onChange={(e) =>
                                  handleEditFormChange('Ticket_ArrestingAgency', e.target.value)
                                }
                              />
                            ) : (
                              <Typography variant="body1" gutterBottom>
                                {ticket.ticket.Ticket_ArrestingAgency || '—'}
                              </Typography>
                            )}
                          </Box>

                          <Box flex={1}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Arresting agency address
                            </Typography>
                            {isEditing ? (
                              <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                size="small"
                                helperText="Street on one line, city/state/ZIP on the next"
                                value={editForm.Ticket_ArrestingAgencyAddress}
                                onChange={(e) =>
                                  handleEditFormChange(
                                    'Ticket_ArrestingAgencyAddress',
                                    e.target.value,
                                  )
                                }
                              />
                            ) : (
                              <Typography
                                variant="body1"
                                gutterBottom
                                sx={{ whiteSpace: 'pre-line' }}
                              >
                                {ticket.ticket.Ticket_ArrestingAgencyAddress || '—'}
                              </Typography>
                            )}
                          </Box>
                        </Stack>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <Box flex={1}>
                            <Typography variant="subtitle2" color="text.secondary">
                              City
                            </Typography>
                            {isEditing ? (
                              <TextField
                                fullWidth
                                size="small"
                                value={editForm.Ticket_City}
                                onChange={(e) =>
                                  handleEditFormChange(
                                    'Ticket_City',
                                    e.target.value,
                                  )
                                }
                              />
                            ) : (
                              <Typography variant="body1" gutterBottom>
                                {ticket.ticket.Ticket_City}
                              </Typography>
                            )}
                          </Box>

                          <Box flex={1}>
                            <Typography variant="subtitle2" color="text.secondary">
                              County
                            </Typography>
                            {isEditing ? (
                              <TextField
                                fullWidth
                                size="small"
                                value={editForm.Ticket_County}
                                onChange={(e) =>
                                  handleEditFormChange(
                                    'Ticket_County',
                                    e.target.value,
                                  )
                                }
                              />
                            ) : (
                              <Typography variant="body1" gutterBottom>
                                {ticket.ticket.Ticket_County}
                              </Typography>
                            )}
                          </Box>
                        </Stack>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Violation Address
                          </Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              value={editForm.Ticket_Address}
                              onChange={(e) =>
                                handleEditFormChange(
                                  'Ticket_Address',
                                  e.target.value,
                                )
                              }
                            />
                          ) : (
                            <Typography variant="body1" gutterBottom>
                              {ticket.ticket.Ticket_Address}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </Collapse>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <Box flex={1}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Court Date
                        </Typography>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            size="small"
                            type="date"
                            value={editForm.Ticket_CourtDate}
                            onChange={(e) =>
                              handleEditFormChange(
                                'Ticket_CourtDate',
                                e.target.value,
                              )
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        ) : (
                          <Box>
                            <Typography variant="body1">
                              {ticket.ticket.Ticket_CourtDate
                                ? formatDate(ticket.ticket.Ticket_CourtDate)
                                : 'Not set'}
                            </Typography>
                            {ticket.ticket.Ticket_CourtDateManualOverride ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  mt: 0.5,
                                }}
                              >
                                <Chip
                                  label="Manually set"
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                />
                                <Button
                                  size="small"
                                  onClick={handleResetCourtDateToAuto}
                                  disabled={actionLoading}
                                >
                                  Reset to auto
                                </Button>
                              </Box>
                            ) : courtDateFromEvent ? (
                              <Chip
                                label="Auto from court events"
                                size="small"
                                variant="outlined"
                                sx={{ mt: 0.5 }}
                              />
                            ) : ticket.ticket.Ticket_CourtDate && !courtEventsLoading ? (
                              <Chip
                                label="From the ticket, no court setting"
                                size="small"
                                variant="outlined"
                                sx={{ mt: 0.5 }}
                              />
                            ) : null}
                          </Box>
                        )}
                      </Box>

                      <Box flex={1}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Created At
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {formatDate(ticket.ticket.Ticket_CreatedAt)}
                        </Typography>
                      </Box>
                    </Stack>

                    <Collapse in={showCaseData} unmountOnExit>
                      <Stack spacing={2}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <Box flex={1}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Driver&apos;s License #
                            </Typography>
                            {/*
                              Read-only, and edited on the client record instead.

                              There were two licence columns, one on the case and one on the client,
                              and they could disagree. A bond and a letter of representation on the
                              same case could carry different numbers. The client's is now the one
                              that prints everywhere, so a box here that edited the case's would let
                              staff type a number that never appears on anything.
                            */}
                            <Typography variant="body1" gutterBottom>
                              {ticket.user?.User_DriversLicenseNumber ||
                                ticket.ticket.Ticket_DriversLicenseNumber ||
                                '—'}
                            </Typography>
                            {isEditing && ticket.user?.User_guid && (
                              <MuiLink
                                component={Link}
                                to={`/user?User_guid=${ticket.user.User_guid}`}
                                variant="caption"
                                underline="hover"
                              >
                                Edit on the client record
                              </MuiLink>
                            )}
                          </Box>
                          <Box flex={1}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Date of Birth
                            </Typography>
                            {/*
                              Editable here, unlike the licence number above, because this is the
                              field that blocks the case. Age picks the letter of representation, so
                              a case cannot be opened without it, and staff were being sent back to
                              the client record to supply it (Jett, 18 Sep 2026). It saves to the
                              client, which is the one place it is stored.
                            */}
                            {/* Guarded like the licence link above: with no client on the case
                                there is nothing to save to, and an editable box would take a date
                                and discard it under a success message. */}
                            {isEditing && ticket.user?.User_guid ? (
                              <TextField
                                type="date"
                                size="small"
                                fullWidth
                                value={clientDob}
                                onChange={(e) => setClientDob(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                helperText="Saved on the client record"
                              />
                            ) : (
                              <Typography variant="body1" gutterBottom>
                                {ticket.user?.User_DOB
                                  ? formatDateOnly(ticket.user.User_DOB)
                                  : '—'}
                              </Typography>
                            )}
                          </Box>
                          <Box flex={1}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Gender
                            </Typography>
                            {isEditing ? (
                              <GenderPronounFields
                                gender={editForm.Ticket_Gender as TicketGender}
                                pronounSet={editForm.Ticket_PronounSet as PronounSet}
                                onGenderChange={(gender, pronounSet) => {
                                  setEditForm((prev) => ({
                                    ...prev,
                                    Ticket_Gender: gender,
                                    Ticket_PronounSet: pronounSet,
                                  }));
                                }}
                                genderLabel="Gender"
                              />
                            ) : (
                              <Typography variant="body1" gutterBottom>
                                {formatGenderLabel(ticket.ticket.Ticket_Gender)}
                                {formatPronounSetLabel(
                                  ticket.ticket.Ticket_PronounSet,
                                )
                                  ? ` · ${formatPronounSetLabel(ticket.ticket.Ticket_PronounSet)}`
                                  : ''}
                              </Typography>
                            )}
                          </Box>
                          <Box flex={1}>
                            <Typography variant="subtitle2" color="text.secondary">
                              DSC in past year
                            </Typography>
                            {isEditing ? (
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={editForm.Ticket_HasTakenDscInPastYear}
                                    onChange={(e) =>
                                      setEditForm((prev) => ({
                                        ...prev,
                                        Ticket_HasTakenDscInPastYear:
                                          e.target.checked,
                                      }))
                                    }
                                  />
                                }
                                label="Has taken DSC"
                              />
                            ) : (
                              <Typography variant="body1" gutterBottom>
                                {ticket.ticket.Ticket_HasTakenDscInPastYear === true
                                  ? 'Yes'
                                  : ticket.ticket.Ticket_HasTakenDscInPastYear ===
                                      false
                                    ? 'No'
                                    : '—'}
                              </Typography>
                            )}
                          </Box>
                        </Stack>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <Box flex={1}>
                            <Typography variant="subtitle2" color="text.secondary">
                              License Type
                            </Typography>
                            {isEditing ? (
                              <FormControl fullWidth size="small">
                                <Select
                                  value={editForm.Ticket_LicenseType}
                                  onChange={(e) =>
                                    handleEditFormChange(
                                      'Ticket_LicenseType',
                                      e.target.value,
                                    )
                                  }
                                >
                                  <MenuItem value="Class C">Class C</MenuItem>
                                  <MenuItem value="CDL">CDL</MenuItem>
                                  <MenuItem value="None">No License</MenuItem>
                                </Select>
                              </FormControl>
                            ) : (
                              <Typography variant="body1" gutterBottom>
                                {ticket.ticket.Ticket_LicenseType}
                              </Typography>
                            )}
                          </Box>

                          <Box flex={1}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Representation Plan
                            </Typography>
                            {isEditing ? (
                              <FormControl fullWidth size="small">
                                <Select
                                  value={editForm.Ticket_Representation}
                                  onChange={(e) =>
                                    handleEditFormChange(
                                      'Ticket_Representation',
                                      e.target.value,
                                    )
                                  }
                                  disabled={
                                    loadingProducts || matchingProducts.length === 0
                                  }
                                >
                                  {loadingProducts ? (
                                    <MenuItem disabled>
                                      Loading pricing options...
                                    </MenuItem>
                                  ) : matchingProducts.length === 0 ? (
                                    <MenuItem disabled>
                                      No matching plans available
                                    </MenuItem>
                                  ) : (
                                    matchingProducts.map((product) => (
                                      <MenuItem
                                        key={product.Product_guid}
                                        value={product.Product_Name}
                                      >
                                        {product.Product_Name} — $
                                        {(product.Product_Cost / 100).toFixed(2)}
                                      </MenuItem>
                                    ))
                                  )}
                                </Select>
                              </FormControl>
                            ) : (
                              <Typography variant="body1" gutterBottom>
                                {ticket.ticket.Ticket_Representation}
                              </Typography>
                            )}
                          </Box>
                        </Stack>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <Box flex={1}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Zone
                            </Typography>
                            {isEditing ? (
                              <FormControl fullWidth size="small">
                                <Select
                                  value={editForm.Ticket_Zone}
                                  onChange={(e) =>
                                    handleEditFormChange(
                                      'Ticket_Zone',
                                      e.target.value,
                                    )
                                  }
                                >
                                  <MenuItem value="Street">Street</MenuItem>
                                  <MenuItem value="Highway">Highway</MenuItem>
                                  <MenuItem value="Interstate">Interstate</MenuItem>
                                  <MenuItem value="Parking">Parking</MenuItem>
                                  <MenuItem value="School Zone">
                                    School Zone
                                  </MenuItem>
                                  <MenuItem value="Construction Zone">
                                    Construction Zone
                                  </MenuItem>
                                  <MenuItem value="Other">Other</MenuItem>
                                </Select>
                              </FormControl>
                            ) : (
                              <Typography variant="body1" gutterBottom>
                                {ticket.ticket.Ticket_Zone}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </Stack>
                    </Collapse>

                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Court
                        <Typography
                          variant="subtitle1"
                          sx={{
                            cursor: 'pointer',
                            color: 'primary.main',
                            '&:hover': { textDecoration: 'underline' },
                            display: 'inline-flex',
                            alignItems: 'center',
                            marginLeft: 1,
                            gap: 0.5,
                          }}
                          onClick={handleOpenCourtInfo}
                        >
                          <Info fontSize="small" />
                          Info
                        </Typography>
                      </Typography>
                      {isEditing ? (
                        <CourtSelect
                          value={selectedCourt}
                          onChange={(court) => {
                            setSelectedCourt(court);
                            // Reset prosecutor when court changes
                            if (
                              selectedProsecutor &&
                              selectedProsecutor.court?.Court_guid !==
                                court?.Court_guid
                            ) {
                              setSelectedProsecutor(null);
                            }
                          }}
                          label=""
                          placeholder="Search and select a court..."
                        />
                      ) : ticket.court?.Court_guid ? (
                        <Box>
                          <Typography variant="body1">
                            <MuiLink
                              component={Link}
                              to={`/court?Court_guid=${ticket.court.Court_guid}`}
                              target="_blank"
                              rel="noopener"
                              underline="hover"
                            >
                              {ticket.court.Court_Name}
                            </MuiLink>
                          </Typography>
                          {/*
                            Dani, Aug 19: "really the only thing I use is the court contact
                            information (phone, email)." It was on the case all along - the DTO
                            carries it - but the only way to see it was the Info dialog, which
                            fires two more API calls to show a number already in hand. The one
                            field she uses on every case took three clicks and a wait.

                            The email is shown through the server's own classification rather than
                            printed raw: several hundred courts hold a placeholder address, and an
                            address that looks usable but is not is worse than none, because
                            somebody would send to it by hand.
                          */}
                          <Stack
                            direction="row"
                            spacing={2}
                            flexWrap="wrap"
                            alignItems="center"
                            sx={{ mt: 0.5 }}
                          >
                            {ticket.court.Court_Phone ? (
                              <MuiLink
                                href={`tel:${ticket.court.Court_Phone}`}
                                underline="hover"
                                variant="body2"
                              >
                                {ticket.court.Court_Phone}
                              </MuiLink>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No phone on file
                              </Typography>
                            )}

                            {ticket.courtEmailStatus === 'valid' &&
                            ticket.court.Court_Email ? (
                              <MuiLink
                                href={`mailto:${ticket.court.Court_Email}`}
                                underline="hover"
                                variant="body2"
                              >
                                {ticket.court.Court_Email}
                              </MuiLink>
                            ) : (
                              <Tooltip
                                arrow
                                title={
                                  courtEmailWarning ??
                                  'No usable email address is on file for this court.'
                                }
                              >
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  label={
                                    ticket.courtEmailStatus === 'placeholder'
                                      ? 'Email is a placeholder'
                                      : 'No email on file'
                                  }
                                />
                              </Tooltip>
                            )}

                            {ticket.court.Court_Fax && (
                              <Typography variant="body2" color="text.secondary">
                                Fax {ticket.court.Court_Fax}
                              </Typography>
                            )}
                          </Stack>
                        </Box>
                      ) : (
                        <Typography variant="body1" gutterBottom>
                          {ticket.court?.Court_Name || 'No court assigned'}
                        </Typography>
                      )}
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Prosecutor
                      </Typography>
                      {isEditing ? (
                        <ProsecutorSelect
                          value={selectedProsecutor}
                          onChange={setSelectedProsecutor}
                          label=""
                          placeholder="Search and select a prosecutor..."
                          disabled={!selectedCourt}
                          courtGuidFilter={selectedCourt?.Court_guid}
                          helperText={
                            !selectedCourt
                              ? 'Please select a court first'
                              : undefined
                          }
                        />
                      ) : ticket.prosecutor ? (
                        <Box>
                          <Typography variant="body1">
                            {`${ticket.prosecutor.Prosecutor_Name} (${ticket.prosecutor.court?.Court_Name || ticket.court?.Court_Name || 'Unknown Court'})`}
                          </Typography>
                          {/*
                            Alongside the court's details for the same reason: Kelsi's second step
                            on every case is getting hold of this person, and it was behind the
                            same dialog.
                          */}
                          <Stack
                            direction="row"
                            spacing={2}
                            flexWrap="wrap"
                            alignItems="center"
                            sx={{ mt: 0.5 }}
                          >
                            {ticket.prosecutor.Prosecutor_Phone && (
                              <MuiLink
                                href={`tel:${ticket.prosecutor.Prosecutor_Phone}`}
                                underline="hover"
                                variant="body2"
                              >
                                {ticket.prosecutor.Prosecutor_Phone}
                              </MuiLink>
                            )}
                            {ticket.prosecutor.Prosecutor_Email && (
                              <MuiLink
                                href={`mailto:${ticket.prosecutor.Prosecutor_Email}`}
                                underline="hover"
                                variant="body2"
                              >
                                {ticket.prosecutor.Prosecutor_Email}
                              </MuiLink>
                            )}
                            {!ticket.prosecutor.Prosecutor_Phone &&
                              !ticket.prosecutor.Prosecutor_Email && (
                                <Typography variant="body2" color="text.secondary">
                                  No contact details on file
                                </Typography>
                              )}
                          </Stack>
                        </Box>
                      ) : (
                        <Typography variant="body1" gutterBottom>
                          No prosecutor assigned
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Box>

            {/* Status and Actions */}
            <Box flex={1} minWidth="300px">
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Current Status
                  </Typography>

                  {/*
                    Whether this case may be discussed outside the firm (Lloyd, 15 Sep 2026). It sits
                    with the status rather than in a tab because the question comes up when somebody
                    is already looking at the case, deciding whether to answer a partner's email.
                  */}
                  {ticket?.ticket?.Ticket_guid && (
                    <Box mb={2}>
                      <CaseConsentChip Ticket_guid={ticket.ticket.Ticket_guid} />
                    </Box>
                  )}

                  {ticket.ticketStatus && (
                    <Box mb={2}>
                      <Chip
                        label={ticket.ticketStatus.TicketStatus_Name}
                        color={
                          getStatusColor(
                            ticket.ticketStatus.TicketStatus_Name,
                          ) as any
                        }
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="body1" color="text.primary">
                        {ticket.ticketStatus.TicketStatus_Desc}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Updated:{' '}
                        {formatDate(ticket.ticketStatus.TicketStatus_DateTime)}
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/*
                    Which letter this case gets, and why (Maria Elena, Aug 7).

                    She saw a 21-year-old receive the "over 25" letter and re-made it by hand in
                    Clio. The routing was right — his citation read 84 in a 55, and at 25+ over the
                    limit a driving safety course is not legally available, so the letter has to ask
                    for a deferral. But that template shares its name with the one used for clients
                    over 25 YEARS OLD, so from this screen it looked like a mistake.

                    The server already worked this out and wrote it to a log nobody reads. Showing it
                    here is what stops someone redoing correct work.
                  */}
                  {ticket.lorRouting && isTrafficMatter(ticket.ticket.Ticket_MatterType) && (
                    <Box mb={2}>
                      <Typography variant="h6" gutterBottom>
                        Letter of representation
                      </Typography>
                      <Alert severity={ticket.lorRouting.needsStaffReview ? 'warning' : 'info'}>
                        <strong>{describeLorTemplate(ticket.lorRouting.templateType)}</strong>
                        <br />
                        {ticket.lorRouting.reason}
                        {ticket.lorRouting.age === null && (
                          <>
                            <br />
                            This client has no date of birth on file, so the age-based part of this
                            decision was made without one. Add it if the letter looks wrong.
                          </>
                        )}
                      </Alert>

                      {/*
                        The picker (Maria Elena, Aug 13). Before this the routing was read-only, so
                        a case the rules got wrong — or one carrying no date for them to read — had
                        to be re-made by hand in Clio. Choosing here changes the letter this case
                        generates from now on; it does not touch a letter already sent.
                      */}
                      <TextField
                        select
                        size="small"
                        fullWidth
                        sx={{ mt: 2 }}
                        label="Letter to use"
                        value={ticket.ticket.Ticket_LorTemplateOverride ?? ''}
                        disabled={lorOverrideSaving}
                        onChange={(e) => handleLorOverrideChange(e.target.value)}
                      >
                        <MenuItem value="">
                          Choose automatically (
                          {describeLorTemplate(ticket.lorRouting.automaticTemplateType)})
                        </MenuItem>
                        {LOR_TEMPLATE_CHOICES.map((code) => (
                          <MenuItem key={code} value={code}>
                            {describeLorTemplate(code)}
                          </MenuItem>
                        ))}
                      </TextField>

                      {ticket.lorRouting.isManualOverride && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          This letter was picked by hand, so the automatic rules no longer apply to
                          this case — including the check for a citation over two years old. Set it
                          back to <strong>Choose automatically</strong> if that is not what you want.
                        </Alert>
                      )}

                      {/*
                        Two different "no offense date" situations, and they need different advice.
                        A usable court date means the age check ran on a stand-in; no usable date —
                        including the 2000-12-01 import sentinel the rule ignores — means the age
                        check could not run at all, and saying it "used the court date" would be a
                        lie about a case that has none.
                      */}
                      {!ticket.lorRouting.isManualOverride &&
                        !ticket.lorRouting.offenseDate &&
                        ticket.lorRouting.citationAgeSource === 'court-date' && (
                          <Alert severity="info" sx={{ mt: 1 }}>
                            No offense date on file, so the age of this citation was judged from the
                            court date. Adding the real offense date above makes that check reliable.
                          </Alert>
                        )}

                      {!ticket.lorRouting.isManualOverride &&
                        ticket.lorRouting.citationAgeSource === null && (
                          <Alert severity="info" sx={{ mt: 1 }}>
                            This case has no usable date, so the check for a citation over two years
                            old could not run. If this ticket is an old one, add the offense date
                            above or pick the letter yourself.
                          </Alert>
                        )}

                      <Typography variant="caption" color="text.secondary">
                        Based on the case details as they stand now. If the citation or licence type
                        was edited after the letter was created, regenerate it to match.
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/*
                    Case type. Sits immediately above Actions because that is where it changes what
                    happens: a non-traffic matter approves without a ticket number and without a
                    letter of representation going to a court that should not receive one.
                  */}
                  <Box mb={2}>
                    <Typography variant="h6" gutterBottom>
                      Case type
                    </Typography>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label="Case type"
                      value={
                        isTrafficMatter(ticket.ticket.Ticket_MatterType)
                          ? 'TRAFFIC'
                          : 'NON_TRAFFIC'
                      }
                      disabled={matterTypeSaving}
                      onChange={(e) => handleMatterTypeChange(e.target.value)}
                    >
                      {MATTER_TYPES.map((code) => (
                        <MenuItem key={code} value={code}>
                          {MATTER_TYPE_LABELS[code]}
                        </MenuItem>
                      ))}
                    </TextField>
                    {!isTrafficMatter(ticket.ticket.Ticket_MatterType) && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        {NON_TRAFFIC_EXPLANATION}
                      </Alert>
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Approve button for approving status */}
                  {isApprovingStatus && (
                    <Box mb={2}>
                      <Typography variant="h6" gutterBottom>
                        Actions
                      </Typography>
                      {approvalBlockReason && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          {approvalBlockReason}
                        </Alert>
                      )}
                      {/*
                        Non-blocking: staff can still approve. This exists so nobody discovers after
                        the fact that the court copy went to the internal mailbox — previously the
                        only clue was an empty Drafts folder.
                      */}
                      {courtEmailWarning && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          {courtEmailWarning}{' '}
                          {ticket.court?.Court_guid && (
                            <Link
                              to={`/court?Court_guid=${ticket.court.Court_guid}`}
                            >
                              Edit this court
                            </Link>
                          )}
                        </Alert>
                      )}
                      <Tooltip
                        title={approvalBlockReason ?? ''}
                        disableHoverListener={!approvalBlockReason}
                      >
                        <span>
                          <Button
                            variant="contained"
                            color="success"
                            fullWidth
                            onClick={handleApproveTicket}
                            disabled={
                              actionLoading || Boolean(approvalBlockReason)
                            }
                            sx={{ mb: 2 }}
                          >
                            {actionLoading ? (
                              <CircularProgress size={20} />
                            ) : (
                              'Approve Ticket'
                            )}
                          </Button>
                        </span>
                      </Tooltip>
                      <Typography variant="caption" color="text.secondary">
                        This will send an email to the user with the letter of
                        representation.
                      </Typography>
                    </Box>
                  )}

                  {lorFollowUpStarted && (
                    <Box mb={2}>
                      <Typography variant="h6" gutterBottom>
                        LOR Court Confirmation
                      </Typography>
                      {lorFollowUpConfirmed ? (
                        <Chip
                          label="Confirmed — follow-ups stopped"
                          color="success"
                          sx={{ mb: 1 }}
                        />
                      ) : (
                        <Chip
                          label="Awaiting court confirmation"
                          color="warning"
                          sx={{ mb: 1 }}
                        />
                      )}
                      {ticket.ticket.Ticket_LorSentAt && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mb: 1 }}
                        >
                          LOR sent: {formatDate(ticket.ticket.Ticket_LorSentAt)}
                          {ticket.ticket.Ticket_LorEscalationStage != null &&
                          ticket.ticket.Ticket_LorEscalationStage > 0
                            ? ` · Stage ${ticket.ticket.Ticket_LorEscalationStage} sent`
                            : ''}
                        </Typography>
                      )}
                      {lorFollowUpActive && (
                        <Button
                          variant="contained"
                          color="primary"
                          fullWidth
                          onClick={handleConfirmLorFollowUp}
                          disabled={actionLoading}
                          sx={{ mb: 1 }}
                        >
                          Mark as Confirmed
                        </Button>
                      )}
                      {lorFollowUpConfirmed && (
                        <Stack spacing={1}>
                          <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => handleRestartLorFollowUp(1)}
                            disabled={actionLoading}
                          >
                            Restart Automation (from Day 3)
                          </Button>
                          <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => handleRestartLorFollowUp(2)}
                            disabled={actionLoading}
                          >
                            Restart Automation (from Day 7)
                          </Button>
                        </Stack>
                      )}

                    </Box>
                  )}

                  {/*
                    Outside the follow-up block on purpose.
                    ---------------------------------------
                    This used to live inside it, so it only appeared once Ticket_LorSent was true —
                    which meant the one case that most needed a letter, the case that has none,
                    was the only case that could not get one. Approve is offered solely while the
                    status is "citation received (Approving)", so anything further along with no
                    letter had no route at all: approve said "already approved", rebuild was not on
                    screen (Janneleth C. Morales Garcia — Maria Elena, Aug 12).

                    Non-traffic matters are excluded because they have no LOR by design, and the
                    ticket-number requirement is surfaced the same way approval surfaces it rather
                    than letting the server reject the click.
                  */}
                  {isTrafficMatter(ticket.ticket.Ticket_MatterType) && (
                    <Box mb={2}>
                      <Typography variant="h6" gutterBottom>
                        Letter of Representation
                      </Typography>
                      {lorBlockReason && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          {lorBlockReason}
                        </Alert>
                      )}
                      <Tooltip
                        title={lorBlockReason ?? ''}
                        disableHoverListener={!lorBlockReason}
                      >
                        <span>
                          <Button
                            variant="outlined"
                            fullWidth
                            onClick={handleRebuildLor}
                            disabled={actionLoading || Boolean(lorBlockReason)}
                          >
                            {lorFollowUpStarted
                              ? 'Rebuild Letter from Case Details'
                              : 'Generate Letter from Case Details'}
                          </Button>
                        </span>
                      </Tooltip>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5 }}
                      >
                        {lorFollowUpStarted
                          ? 'Correct the case first, then rebuild. Creates fresh drafts for review — the previous drafts are not removed.'
                          : 'No letter has been generated for this case. Creates drafts for review and starts the court follow-up schedule.'}
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/*
                    Offer decision quick actions.

                    Outlined, not contained-success. "Accept Offer" used to be the same full-width
                    green button as "Approve Ticket" a divider above it, and staff pressed the wrong
                    one — see handleOfferDecision for the seven cases that proves. These record
                    something that already happened elsewhere; they are not the primary action on
                    this screen and should not look like it.
                  */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Offer
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 1 }}
                    >
                      Records a decision the client has already given you. Both are visible to the
                      client.
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="outlined"
                        color="success"
                        fullWidth
                        onClick={() => handleOfferDecision('accept')}
                        disabled={actionLoading}
                      >
                        Accept Offer
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        onClick={() => handleOfferDecision('reject')}
                        disabled={actionLoading}
                      >
                        Reject Offer
                      </Button>
                    </Stack>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  {/* Status update form */}
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Update Status
                    </Typography>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>New Status</InputLabel>
                      <Select
                        value={newStatus}
                        label="New Status"
                        onChange={handleStatusChange}
                      >
                        {statusOptions.map((status) => (
                          <MenuItem key={status} value={status}>
                            {status.charAt(0).toUpperCase() +
                              status.slice(1).replace('_', ' ')}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* The description is genuinely required — the server rejects a status update
                        without one (1–500 chars). Nothing said so, and the button below disables on
                        an empty description, so it read as "the button is broken on every case".
                        Reported by Hannah, Aug 7: Accept/Reject work because they only gate on
                        actionLoading. Marking it required and saying why is the whole fix. */}
                    <TextField
                      required
                      fullWidth
                      multiline
                      rows={3}
                      label="Status Description"
                      value={statusDescription}
                      onChange={(e) => setStatusDescription(e.target.value)}
                      sx={{ mb: 2 }}
                      placeholder="Enter a description for this status update..."
                      helperText="Required. Update Status stays disabled until you pick a status and enter a description."
                    />

                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleStatusUpdate}
                      disabled={
                        actionLoading || !newStatus || !statusDescription.trim()
                      }
                    >
                      {actionLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        'Update Status'
                      )}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Stack>


          {/* Court Information Modal */}
          <Dialog
            open={showCourtInfoModal}
            onClose={handleCloseCourtInfo}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Court and Prosecutor Information</DialogTitle>
            <DialogContent>
              {courtInfoLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : courtInfoError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {courtInfoError}
                </Alert>
              ) : (
                <Stack spacing={3} sx={{ mt: 1 }}>
                  {/* Court Information */}
                  {detailedCourtInfo ? (
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" color="primary" gutterBottom>
                          Court Information
                        </Typography>
                        <Stack spacing={2}>
                          <Box
                            sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}
                          >
                            <Box sx={{ flex: '1 1 300px' }}>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                              >
                                Court Name
                              </Typography>
                              <Typography variant="body1" gutterBottom>
                                {detailedCourtInfo.Court_Name}
                              </Typography>
                            </Box>
                            <Box sx={{ flex: '1 1 200px' }}>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                              >
                                Status
                              </Typography>
                              <Chip
                                label={
                                  detailedCourtInfo.Court_Active
                                    ? 'Active'
                                    : 'Inactive'
                                }
                                color={
                                  detailedCourtInfo.Court_Active
                                    ? 'success'
                                    : 'error'
                                }
                                size="small"
                              />
                            </Box>
                          </Box>
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              Address
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              {detailedCourtInfo.Court_Address}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              Email
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              {detailedCourtInfo.Court_Email}
                            </Typography>
                          </Box>
                          {detailedCourtInfo.Court_Phone && (
                            <Box>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                              >
                                Phone
                              </Typography>
                              <Typography variant="body1" gutterBottom>
                                {detailedCourtInfo.Court_Phone}
                              </Typography>
                            </Box>
                          )}
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              Court ID
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ fontFamily: 'monospace' }}
                            >
                              {detailedCourtInfo.Court_guid}
                            </Typography>
                          </Box>
                          {detailedCourtInfo.prosecutors &&
                            detailedCourtInfo.prosecutors.length > 0 && (
                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                  gutterBottom
                                >
                                  Associated Prosecutors (
                                  {detailedCourtInfo.prosecutors.length})
                                </Typography>
                                <Stack spacing={1}>
                                  {detailedCourtInfo.prosecutors.map(
                                    (prosecutor) => (
                                      <Box
                                        key={prosecutor.Prosecutor_guid}
                                        sx={{
                                          p: 1,
                                          border: '1px solid',
                                          borderColor: 'divider',
                                          borderRadius: 1,
                                          backgroundColor: 'action.hover',
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          fontWeight="medium"
                                        >
                                          {prosecutor.Prosecutor_Name}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          {prosecutor.Prosecutor_Email}
                                        </Typography>
                                        <Chip
                                          label={
                                            prosecutor.Prosecutor_Active
                                              ? 'Active'
                                              : 'Inactive'
                                          }
                                          color={
                                            prosecutor.Prosecutor_Active
                                              ? 'success'
                                              : 'error'
                                          }
                                          size="small"
                                          sx={{ ml: 1 }}
                                        />
                                      </Box>
                                    ),
                                  )}
                                </Stack>
                              </Box>
                            )}
                        </Stack>
                      </CardContent>
                    </Card>
                  ) : ticket?.court ? (
                    <Alert severity="info">
                      Court information could not be loaded. Basic info:{' '}
                      {ticket.court.Court_Name}
                    </Alert>
                  ) : (
                    <Alert severity="warning">
                      No court assigned to this ticket.
                    </Alert>
                  )}

                  {/* Prosecutor Information */}
                  {detailedProsecutorInfo ? (
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" color="primary" gutterBottom>
                          Assigned Prosecutor
                        </Typography>
                        <Stack spacing={2}>
                          <Box
                            sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}
                          >
                            <Box sx={{ flex: '1 1 300px' }}>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                              >
                                Prosecutor Name
                              </Typography>
                              <Typography variant="body1" gutterBottom>
                                {detailedProsecutorInfo.Prosecutor_Name}
                              </Typography>
                            </Box>
                            <Box sx={{ flex: '1 1 200px' }}>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                              >
                                Status
                              </Typography>
                              <Chip
                                label={
                                  detailedProsecutorInfo.Prosecutor_Active
                                    ? 'Active'
                                    : 'Inactive'
                                }
                                color={
                                  detailedProsecutorInfo.Prosecutor_Active
                                    ? 'success'
                                    : 'error'
                                }
                                size="small"
                              />
                            </Box>
                          </Box>
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              Email
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              {detailedProsecutorInfo.Prosecutor_Email}
                            </Typography>
                          </Box>
                          {detailedProsecutorInfo.court && (
                            <Box>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                              >
                                Associated Court
                              </Typography>
                              <Box
                                sx={{
                                  p: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  backgroundColor: 'action.hover',
                                }}
                              >
                                <Typography variant="body2" fontWeight="medium">
                                  {detailedProsecutorInfo.court.Court_Name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {detailedProsecutorInfo.court.Court_Address}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              Prosecutor ID
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ fontFamily: 'monospace' }}
                            >
                              {detailedProsecutorInfo.Prosecutor_guid}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  ) : ticket?.prosecutor ? (
                    <Alert severity="info">
                      Prosecutor information could not be loaded. Basic info:{' '}
                      {ticket.prosecutor.Prosecutor_Name}
                    </Alert>
                  ) : (
                    <Alert severity="warning">
                      No prosecutor assigned to this ticket.
                    </Alert>
                  )}
                </Stack>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseCourtInfo}>Close</Button>
              <Button
                variant="contained"
                onClick={handleViewCourt}
                disabled={!detailedCourtInfo}
              >
                View Court
              </Button>
            </DialogActions>
          </Dialog>
        </Box>

      </Box>
    </>
  );
};

export default ManagerTicketView;
